import { __rest, __spreadArray, __assign } from 'tslib';
import classnames from 'classnames';
import * as React from 'react';
import React__default, { useState, useCallback, Fragment, useEffect, useRef, forwardRef, useMemo, useContext } from 'react';
import _inheritsLoose from '@babel/runtime/helpers/esm/inheritsLoose';
import _assertThisInitialized from '@babel/runtime/helpers/esm/assertThisInitialized';
import _createClass from '@babel/runtime/helpers/esm/createClass';
import _wrapNativeSuper from '@babel/runtime/helpers/esm/wrapNativeSuper';
import _asyncToGenerator from '@babel/runtime/helpers/esm/asyncToGenerator';
import _createForOfIteratorHelperLoose from '@babel/runtime/helpers/esm/createForOfIteratorHelperLoose';
import _regeneratorRuntime from '@babel/runtime/regenerator';
import { unstable_batchedUpdates, createPortal } from 'react-dom';
import FocusLock, { AutoFocusInside } from 'react-focus-lock/UI';
import { sidecar } from 'use-sidecar';
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import { usePopper } from 'react-popper';

var DEFAULT_ELEMENT$u = 'section';
var Accordion = /*#__PURE__*/React__default.forwardRef(function AOAccordion(_a, ref) {
  var _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$u : _b,
      children = _a.children,
      className = _a.className,
      _c = _a.alwaysOpen,
      alwaysOpen = _c === void 0 ? false : _c,
      _d = _a.small,
      small = _d === void 0 ? false : _d,
      _f = _a.noBorder,
      noBorder = _f === void 0 ? false : _f,
      props = __rest(_a, ["as", "children", "className", "alwaysOpen", "small", "noBorder"]);

  var _g = useState([]),
      isOpenList = _g[0],
      setIsOpenList = _g[1];

  var handleClickOpenAll = useCallback(function (_e, id) {
    return setIsOpenList(function (currentState) {
      return currentState.includes(id) ? currentState.filter(function (accordionName) {
        return accordionName !== id;
      }) : __spreadArray(__spreadArray([], currentState, true), [id], false);
    });
  }, []);
  var handleClickOpenSingle = useCallback(function (_e, id) {
    return setIsOpenList(function (currentState) {
      return currentState.includes(id) ? [] : [id];
    });
  }, []);
  return /*#__PURE__*/React__default.createElement(ElementType, __assign({
    "data-aods": "accordion"
  }, props, {
    className: classnames(className),
    ref: ref
  }), React__default.Children.map(children, function (child, index) {
    return /*#__PURE__*/React__default.createElement(Fragment, null, /*#__PURE__*/React__default.isValidElement(child) ? /*#__PURE__*/React__default.cloneElement(child, {
      className: classnames(index !== 0 && 'border-t-0', child.props.className),
      onClick: alwaysOpen ? handleClickOpenAll : handleClickOpenSingle,
      small: small,
      noBorder: noBorder,
      isOpenList: isOpenList
    }) : child);
  }));
});

var filterProps = function filterProps(componentProps, filterList) {
  return Object.keys(componentProps).reduce(function (newProps, prop) {
    var _a;

    return filterList.includes(prop) ? newProps : __assign(__assign({}, newProps), (_a = {}, _a[prop] = componentProps[prop], _a));
  }, {});
};

var DEFAULT_ELEMENT$t = 'div';
var AccordionContent = /*#__PURE__*/React__default.forwardRef(function AOAccordionContent(_a, ref) {
  var _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$t : _b,
      children = _a.children,
      className = _a.className,
      _c = _a.isOpen,
      isOpen = _c === void 0 ? false : _c,
      groupName = _a.groupName,
      inheritTextStyle = _a.inheritTextStyle,
      noPadding = _a.noPadding,
      props = __rest(_a, ["as", "children", "className", "isOpen", "groupName", "inheritTextStyle", "noPadding"]);

  return groupName ? /*#__PURE__*/React__default.createElement(ElementType, __assign({
    id: "".concat(groupName, "-content"),
    className: classnames(!isOpen && 'hidden', !inheritTextStyle && 'text-body', !noPadding && 'p-4', className),
    "data-aods": "accordion-content",
    "aria-labelledby": "".concat(groupName, "-header"),
    role: "region"
  }, filterProps(props, ['small', 'onClick']), {
    ref: ref
  }), children) : null;
});

var DEFAULT_ELEMENT$s = 'span';
var AoIcon = /*#__PURE__*/React__default.forwardRef(function AOIcon(_a, ref) {
  var _b;

  var className = _a.className,
      _c = _a.as,
      ElementType = _c === void 0 ? DEFAULT_ELEMENT$s : _c,
      icon = _a.icon,
      size = _a.size,
      labelProp = _a.label,
      props = __rest(_a, ["className", "as", "icon", "size", "label"]);

  var label = labelProp || props['aria-label'];
  var hasDescription = label || props['aria-labelledby'] || props['aria-describedby'];
  return /*#__PURE__*/React__default.createElement(ElementType, __assign({
    "data-aods": "icon"
  }, props, {
    className: classnames('ico', "ico-".concat(icon), (_b = {}, _b["ico-".concat(size)] = size, _b), className),
    "aria-label": label,
    "aria-hidden": !hasDescription || undefined,
    ref: ref
  }));
});

var DEFAULT_ELEMENT$r = 'div';
var AccordionHeader = /*#__PURE__*/React__default.forwardRef(function AOAccordionHeader(_a, ref) {
  var _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$r : _b,
      children = _a.children,
      className = _a.className,
      _onClick = _a.onClick,
      _c = _a.isOpen,
      isOpen = _c === void 0 ? false : _c,
      groupName = _a.groupName,
      _d = _a.closeIcon,
      closeIcon = _d === void 0 ? 'chevron-up' : _d,
      _e = _a.openIcon,
      openIcon = _e === void 0 ? 'chevron-down' : _e,
      small = _a.small,
      icon = _a.icon,
      inheritTextStyle = _a.inheritTextStyle,
      props = __rest(_a, ["as", "children", "className", "onClick", "isOpen", "groupName", "closeIcon", "openIcon", "small", "icon", "inheritTextStyle"]);

  return groupName ? /*#__PURE__*/React__default.createElement(ElementType, __assign({
    "data-aods": "accordion-header",
    className: classnames(className)
  }, props, {
    ref: ref
  }), /*#__PURE__*/React__default.createElement("button", {
    id: "".concat(groupName, "-header"),
    className: classnames(!inheritTextStyle && (small ? 'text-title-sm' : 'text-title'), 'bg-transparent w-full flex items-center justify-between text-left p-4 focus:shadow-outline-blue focus:outline-none border-0'),
    type: "button",
    "aria-expanded": isOpen,
    onClick: function onClick(e) {
      return _onClick === null || _onClick === void 0 ? void 0 : _onClick(e, groupName);
    },
    "aria-controls": "".concat(groupName, "-content")
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "flex items-center flex-grow"
  }, icon && /*#__PURE__*/React__default.createElement(AoIcon, {
    icon: icon,
    className: "text-ongreen mr-4",
    "aria-hidden": "true",
    size: "2x"
  }), children), /*#__PURE__*/React__default.createElement(AoIcon, {
    className: "text-cta-action",
    icon: isOpen ? closeIcon : openIcon,
    "aria-hidden": "true",
    size: small ? 'lg' : 'xl'
  }))) : null;
});

var DEFAULT_ELEMENT$q = 'div';
var AccordionItem = /*#__PURE__*/React__default.forwardRef(function AOAccordionItem(_a, ref) {
  var _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$q : _b,
      children = _a.children,
      className = _a.className,
      onClick = _a.onClick,
      _c = _a.isOpen,
      isOpen = _c === void 0 ? false : _c,
      _d = _a.isOpenList,
      isOpenList = _d === void 0 ? [] : _d,
      groupName = _a.groupName,
      _e = _a.small,
      small = _e === void 0 ? false : _e,
      _f = _a.noBorder,
      noBorder = _f === void 0 ? false : _f,
      props = __rest(_a, ["as", "children", "className", "onClick", "isOpen", "isOpenList", "groupName", "small", "noBorder"]);

  return /*#__PURE__*/React__default.createElement(ElementType, __assign({
    "data-aods": "accordion-item"
  }, props, {
    className: classnames(!noBorder && 'border border-l-0 border-r-0 border-gray-40', className),
    ref: ref
  }), React__default.Children.map(children, function (child) {
    return /*#__PURE__*/React__default.createElement(Fragment, null, /*#__PURE__*/React__default.isValidElement(child) ? /*#__PURE__*/React__default.cloneElement(child, {
      onClick: onClick || child.props.onClick,
      isOpen: isOpenList.includes(groupName) || isOpen,
      groupName: groupName,
      small: small
    }) : child);
  }));
});

var DEFAULT_ELEMENT$p = 'nav';
var Breadcrumb = /*#__PURE__*/React__default.forwardRef(function AOBreadcrumb(_a, ref) {
  var children = _a.children,
      _b = _a["as"],
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$p : _b,
      _c = _a["aria-label"],
      ariaLabel = _c === void 0 ? 'Breadcrumbs' : _c,
      inheritTextStyle = _a.inheritTextStyle,
      dividerClassName = _a.dividerClassName,
      props = __rest(_a, ["children", 'as', 'aria-label', "inheritTextStyle", "dividerClassName"]);

  return /*#__PURE__*/React__default.createElement(ElementType, __assign({
    "data-aods": "breadcrumb"
  }, props, {
    ref: ref,
    "aria-label": ariaLabel
  }), /*#__PURE__*/React__default.createElement("ol", {
    className: classnames('flex flex-wrap items-center list-none', !inheritTextStyle && 'text-body')
  }, React__default.Children.map(children, function (child, index) {
    var isLast = index === React__default.Children.count(children) - 1;
    return /*#__PURE__*/React__default.createElement(Fragment, null, /*#__PURE__*/React__default.createElement("li", {
      className: "flex items-center"
    }, /*#__PURE__*/React__default.isValidElement(child) ? /*#__PURE__*/React__default.cloneElement(child, {
      active: isLast
    }) : child, !isLast && /*#__PURE__*/React__default.createElement("span", {
      "aria-hidden": "true",
      className: dividerClassName || 'px-2 text-lg'
    }, "/")));
  })));
});

var DEFAULT_ELEMENT$o = 'a';
var BreadcrumbItem = /*#__PURE__*/React__default.forwardRef(function AOBreadcrumbItem(_a, ref) {
  var _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$o : _b,
      active = _a.active,
      children = _a.children,
      className = _a.className,
      href = _a.href,
      props = __rest(_a, ["as", "active", "children", "className", "href"]);

  return /*#__PURE__*/React__default.createElement(ElementType, __assign({
    className: classnames('no-underline', active ? 'text-secondary font-bold' : 'text-link', className),
    "aria-current": active ? 'page' : undefined,
    href: href,
    "data-aods": "breadcrumb-item"
  }, props, {
    ref: ref
  }), children);
});

var BUTTON_VARIANTS = {
  primary: 'btn-primary',
  secondary: 'btn-secondary',
  tertiary: 'btn-tertiary',
  link: 'bg-transparent border-none text-link p-0 min-h-0 min-w-0',
  white: 'btn-white',
  dark: 'btn-dark'
};
var DEFAULT_ELEMENT$n = 'button';
var Button = /*#__PURE__*/React__default.forwardRef(function AOButton(_a, ref) {
  var _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$n : _b,
      children = _a.children,
      className = _a.className,
      color = _a.color,
      _c = _a.variant,
      variant = _c === void 0 ? 'primary' : _c,
      disabled = _a.disabled,
      _d = _a.lg,
      lg = _d === void 0 ? false : _d,
      _e = _a.icon,
      icon = _e === void 0 ? false : _e,
      _f = _a.fullWidth,
      fullWidth = _f === void 0 ? false : _f,
      props = __rest(_a, ["as", "children", "className", "color", "variant", "disabled", "lg", "icon", "fullWidth"]);

  var isButtonElement = ElementType === 'button';
  var isLink = 'href' in props || 'to' in props;
  var buttonVariant = color || variant;
  return /*#__PURE__*/React__default.createElement(ElementType // eslint-disable-next-line no-undefined
  , __assign({
    // eslint-disable-next-line no-undefined
    type: isButtonElement ? 'button' : undefined,
    disabled: isButtonElement && disabled,
    "aria-disabled": disabled,
    // eslint-disable-next-line no-undefined
    role: !isLink && !isButtonElement ? 'button' : undefined,
    className: classnames('btn', icon && 'btn-icon', fullWidth && 'w-full justify-center', lg && 'btn-lg', disabled ? 'btn-inactive' : BUTTON_VARIANTS[buttonVariant], className),
    "data-aods": "button"
  }, props, {
    ref: ref
  }), children);
});

var DEFAULT_ELEMENT$m = 'div';
var Card = /*#__PURE__*/React__default.forwardRef(function AOCard(_a, ref) {
  var _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$m : _b,
      children = _a.children,
      className = _a.className,
      flex = _a.flex,
      inheritTextStyle = _a.inheritTextStyle,
      noBorder = _a.noBorder,
      noPadding = _a.noPadding,
      raised = _a.raised,
      square = _a.square,
      state = _a.state,
      props = __rest(_a, ["as", "children", "className", "flex", "inheritTextStyle", "noBorder", "noPadding", "raised", "square", "state"]);

  var border = {
    'border-gray-40': !state,
    'border-error': state === 'error',
    'border-attention': state === 'attention',
    'border-success': state === 'success',
    'border-highlight': state === 'highlight'
  };
  var background = {
    'bg-white': !state,
    'bg-error': state === 'error',
    'bg-attention': state === 'attention',
    'bg-success': state === 'success',
    'bg-highlight': state === 'highlight'
  };
  return /*#__PURE__*/React__default.createElement(ElementType, __assign({
    className: classnames('border border-solid text-body', !square && 'rounded', !inheritTextStyle && 'text-body', flex && 'flex flex-col', !noPadding && 'p-4 lg:p-6', noBorder ? 'border-transparent' : border, raised === true && 'shadow-4dp', background, className // TODO: opt-in hover effect with 8dp shadow for flat / 12dp for raised
    ),
    "data-aods": "card"
  }, props, {
    ref: ref
  }), children);
});

var DEFAULT_ELEMENT$l = 'hr';
var CardDivider = /*#__PURE__*/React__default.forwardRef(function AOCardDivider(_a, ref) {
  var _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$l : _b,
      children = _a.children,
      className = _a.className,
      state = _a.state,
      noBorder = _a.noBorder,
      noPadding = _a.noPadding,
      noMargin = _a.noMargin,
      props = __rest(_a, ["as", "children", "className", "state", "noBorder", "noPadding", "noMargin"]);

  var border = {
    'border-gray-40': !state,
    'border-error': state === 'error',
    'border-attention': state === 'attention',
    'border-success': state === 'success',
    'border-highlight': state === 'highlight'
  };
  return /*#__PURE__*/React__default.createElement(ElementType, __assign({
    className: classnames('border-0 h-0 border-b border-solid', noBorder ? 'border-transparent' : 'border-gray-40', !noPadding && '-mx-4 lg:-mx-6', !noMargin && (noBorder ? 'my-2 lg:my-3' : 'my-4 lg:my-6'), border, className),
    "data-aods": "card-divider"
  }, props, {
    ref: ref
  }), children);
});

var toggleTypeToInputType = function toggleTypeToInputType(type) {
  if (type === 'radio-button') return 'radio';
  return type;
};

var DEFAULT_ELEMENT$k = 'input';
var ToggleBase = /*#__PURE__*/React__default.forwardRef(function AOToggleBase(_a, ref) {
  var _b = _a.align,
      align = _b === void 0 ? 'left' : _b,
      hideToggle = _a.hideToggle,
      _c = _a.as,
      ElementType = _c === void 0 ? DEFAULT_ELEMENT$k : _c,
      className = _a.className,
      children = _a.children,
      id = _a.id,
      _d = _a.fullWidth,
      fullWidth = _d === void 0 ? false : _d,
      _e = _a.labelProps,
      labelProps = _e === void 0 ? {} : _e,
      pill = _a.pill,
      padded = _a.padded,
      bodyClassName = _a.bodyClassName,
      state = _a.state,
      type = _a.type,
      inheritTextStyle = _a.inheritTextStyle,
      disabled = _a.disabled,
      props = __rest(_a, ["align", "hideToggle", "as", "className", "children", "id", "fullWidth", "labelProps", "pill", "padded", "bodyClassName", "state", "type", "inheritTextStyle", "disabled"]);

  var hasError = !disabled && state === 'error';
  return (
    /*#__PURE__*/
    // Input is nested, can't detect ElementType
    // eslint-disable-next-line jsx-a11y/label-has-for
    React__default.createElement("label", __assign({}, labelProps, {
      htmlFor: id,
      className: classnames(type, fullWidth && 'flex', pill && "".concat(type, "-pill"), padded && !pill && "".concat(type, "-padded"), inheritTextStyle && "".concat(type, "-inherit-text"), align === 'right' && "".concat(type, "-reverse"), hideToggle && "".concat(type, "-no-toggle"), className)
    }), /*#__PURE__*/React__default.createElement(ElementType, __assign({
      "data-aods": type
    }, props, {
      id: id,
      type: toggleTypeToInputType(type),
      ref: ref,
      className: "".concat(type, "_input"),
      disabled: disabled
    })), /*#__PURE__*/React__default.createElement("span", {
      className: classnames("".concat(type, "_body"), bodyClassName, pill && hasError && 'bg-error border-error')
    }, !hideToggle && /*#__PURE__*/React__default.createElement("span", {
      className: classnames("".concat(type, "_icon-container"))
    }, /*#__PURE__*/React__default.createElement("span", {
      className: classnames("".concat(type, "_icon"), hasError && 'text-error border-error')
    })), /*#__PURE__*/React__default.createElement("span", {
      className: classnames("".concat(type, "_label"), hasError && 'text-error')
    }, children)))
  );
});

var Checkbox = /*#__PURE__*/React__default.forwardRef(function AOCheckbox(props, ref) {
  return /*#__PURE__*/React__default.createElement(ToggleBase, __assign({}, props, {
    type: "checkbox",
    ref: ref
  }));
});

var FieldSet = /*#__PURE__*/React__default.forwardRef(function AOFieldSet(_a, ref) {
  var className = _a.className,
      props = __rest(_a, ["className"]);

  return /*#__PURE__*/React__default.createElement("fieldset", __assign({
    "data-aods": "fieldset"
  }, props, {
    className: classnames(className, 'inline-block border-0 p-0 m-0 min-w-0'),
    ref: ref
  }));
});

var Legend = /*#__PURE__*/React__default.forwardRef(function AOLegend(_a, ref) {
  var _b;

  var className = _a.className,
      style = _a.style,
      _c = _a.display,
      display = _c === void 0 ? false : _c,
      _d = _a.noMargin,
      noMargin = _d === void 0 ? false : _d,
      _e = _a.srOnly,
      srOnly = _e === void 0 ? false : _e,
      inheritTextStyle = _a.inheritTextStyle,
      props = __rest(_a, ["className", "style", "display", "noMargin", "srOnly", "inheritTextStyle"]);

  return /*#__PURE__*/React__default.createElement(Fragment, null, /*#__PURE__*/React__default.createElement("legend", __assign({
    "data-aods": "legend"
  }, props, {
    className: srOnly ? 'sr-only' : classnames(className, 'table w-full', display && (_b = {
      'text-display': display && display === 'md'
    }, _b["text-display-".concat(display)] = display && display !== 'md', _b), !inheritTextStyle && !display && 'text-body', !noMargin && (display ? 'mb-4' : 'mb-2')),
    style: __assign(__assign({}, style), {
      float: 'left'
    }),
    ref: ref
  })), /*#__PURE__*/React__default.createElement("span", {
    className: "block",
    style: {
      clear: 'both'
    }
  }));
});

var DEFAULT_ELEMENT$j = FieldSet;
var ToggleBaseGroup = /*#__PURE__*/React__default.forwardRef(function AOToggleBaseGroup(_a, ref) {
  var children = _a.children,
      _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$j : _b,
      _c = _a.align,
      align = _c === void 0 ? 'left' : _c,
      className = _a.className,
      _d = _a.flush,
      flush = _d === void 0 ? false : _d,
      _e = _a.fullWidth,
      fullWidth = _e === void 0 ? false : _e,
      hideLabel = _a.hideLabel,
      _f = _a.hideToggle,
      hideToggle = _f === void 0 ? false : _f,
      _g = _a.inheritTextStyle,
      inheritTextStyle = _g === void 0 ? false : _g,
      inline = _a.inline,
      label = _a.label,
      name = _a.name,
      _h = _a.padded,
      padded = _h === void 0 ? false : _h,
      _j = _a.pill,
      pill = _j === void 0 ? false : _j,
      state = _a.state,
      props = __rest(_a, ["children", "as", "align", "className", "flush", "fullWidth", "hideLabel", "hideToggle", "inheritTextStyle", "inline", "label", "name", "padded", "pill", "state"]);

  return /*#__PURE__*/React__default.createElement(ElementType, __assign({
    className: classnames(className, fullWidth && 'w-full'),
    ref: ref,
    "data-aods": "toggle-base-group"
  }, props), /*#__PURE__*/React__default.createElement(Legend, {
    className: classnames(!hideLabel && 'mb-2'),
    srOnly: hideLabel,
    inheritTextStyle: inheritTextStyle
  }, label), /*#__PURE__*/React__default.createElement("div", {
    className: classnames(fullWidth && 'w-full', inline && 'flex', !flush && "space-".concat(inline ? 'x' : 'y', "-2"), flush && pill && "-space-".concat(inline ? 'x' : 'y', "-px"))
  }, React__default.Children.map(children, function (child, i) {
    var _a;

    if (! /*#__PURE__*/React__default.isValidElement(child)) return child;
    var bodyClassName = child.props.bodyClassName;

    var toggleProps = __assign(__assign({
      name: name,
      pill: pill,
      padded: padded,
      inheritTextStyle: inheritTextStyle,
      align: align,
      hideToggle: hideToggle,
      state: state
    }, child.props), {
      fullWidth: true,
      bodyClassName: classnames(bodyClassName, (_a = {}, _a["rounded-".concat(inline ? 'r' : 'b', "-none")] = flush && i < React__default.Children.count(children) - 1, _a["rounded-".concat(inline ? 'l' : 't', "-none")] = flush && i > 0, _a))
    });

    return /*#__PURE__*/React__default.cloneElement(child, toggleProps);
  })));
});

var CheckboxGroup = function CheckboxGroup(_a) {
  var children = _a.children,
      props = __rest(_a, ["children"]);

  return /*#__PURE__*/React__default.createElement(ToggleBaseGroup, __assign({}, props), children);
};

var DEFAULT_ELEMENT$i = 'div';
var Container = /*#__PURE__*/React__default.forwardRef(function AOContainer(_a, ref) {
  var children = _a.children,
      className = _a.className,
      noMargin = _a.noMargin,
      breakpoint = _a.breakpoint,
      _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$i : _b,
      props = __rest(_a, ["children", "className", "noMargin", "breakpoint", "as"]);

  return /*#__PURE__*/React__default.createElement(ElementType, __assign({
    className: classnames(breakpoint ? ["".concat(breakpoint, ":container"), !noMargin && "".concat(breakpoint, ":p-4")] : ['container', !noMargin && 'p-2 sm:p-4'], 'mx-auto', className),
    "data-aods": "container"
  }, props, {
    ref: ref
  }), children);
});

var defaultConfig = {
  language: 'en',
  // Default to false so behaviour with no provider is expected client side use
  isSSR: false
};
var DesignSystemContext = /*#__PURE__*/React__default.createContext(defaultConfig);

var DesignSystemProvider = function DesignSystemProvider(_a) {
  var children = _a.children,
      userConfig = __rest(_a, ["children"]);

  var _b = useState(Object.assign({}, defaultConfig, // Override default so initial value is true which is overriden by hook on client-side
  {
    isSSR: true
  }, userConfig)),
      config = _b[0],
      setConfig = _b[1];

  useEffect(function () {
    setConfig(function (currentConfig) {
      return __assign(__assign({}, currentConfig), {
        isSSR: false
      });
    });
  }, []);
  return /*#__PURE__*/React__default.createElement(DesignSystemContext.Provider, {
    value: config
  }, children);
};

var updateQueue = /*#__PURE__*/makeQueue();

var raf = function raf(fn) {
  return schedule(fn, updateQueue);
};

var writeQueue = /*#__PURE__*/makeQueue();

raf.write = function (fn) {
  return schedule(fn, writeQueue);
};

var onStartQueue = /*#__PURE__*/makeQueue();

raf.onStart = function (fn) {
  return schedule(fn, onStartQueue);
};

var onFrameQueue = /*#__PURE__*/makeQueue();

raf.onFrame = function (fn) {
  return schedule(fn, onFrameQueue);
};

var onFinishQueue = /*#__PURE__*/makeQueue();

raf.onFinish = function (fn) {
  return schedule(fn, onFinishQueue);
};

var timeouts = [];

raf.setTimeout = function (handler, ms) {
  var time = raf.now() + ms;

  var cancel = function cancel() {
    var i = timeouts.findIndex(function (t) {
      return t.cancel == cancel;
    });
    if (~i) timeouts.splice(i, 1);
    __raf.count -= ~i ? 1 : 0;
  };

  var timeout = {
    time: time,
    handler: handler,
    cancel: cancel
  };
  timeouts.splice(findTimeout(time), 0, timeout);
  __raf.count += 1;
  start();
  return timeout;
};

var findTimeout = function findTimeout(time) {
  return ~(~timeouts.findIndex(function (t) {
    return t.time > time;
  }) || ~timeouts.length);
};

raf.cancel = function (fn) {
  updateQueue.delete(fn);
  writeQueue.delete(fn);
};

raf.sync = function (fn) {
  sync = true;
  raf.batchedUpdates(fn);
  sync = false;
};

raf.throttle = function (fn) {
  var lastArgs;

  function queuedFn() {
    try {
      fn.apply(void 0, lastArgs);
    } finally {
      lastArgs = null;
    }
  }

  function throttled() {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    lastArgs = args;
    raf.onStart(queuedFn);
  }

  throttled.handler = fn;

  throttled.cancel = function () {
    onStartQueue.delete(queuedFn);
    lastArgs = null;
  };

  return throttled;
};

var nativeRaf = typeof window != 'undefined' ? window.requestAnimationFrame : function () {};

raf.use = function (impl) {
  return nativeRaf = impl;
};

raf.now = typeof performance != 'undefined' ? function () {
  return performance.now();
} : Date.now;

raf.batchedUpdates = function (fn) {
  return fn();
};

raf.catch = console.error;
raf.frameLoop = 'always';

raf.advance = function () {
  if (raf.frameLoop !== 'demand') {
    console.warn('Cannot call the manual advancement of rafz whilst frameLoop is not set as demand');
  } else {
    update();
  }
};

var ts = -1;
var sync = false;

function schedule(fn, queue) {
  if (sync) {
    queue.delete(fn);
    fn(0);
  } else {
    queue.add(fn);
    start();
  }
}

function start() {
  if (ts < 0) {
    ts = 0;

    if (raf.frameLoop !== 'demand') {
      nativeRaf(loop);
    }
  }
}

function loop() {
  if (~ts) {
    nativeRaf(loop);
    raf.batchedUpdates(update);
  }
}

function update() {
  var prevTs = ts;
  ts = raf.now();
  var count = findTimeout(ts);

  if (count) {
    eachSafely(timeouts.splice(0, count), function (t) {
      return t.handler();
    });
    __raf.count -= count;
  }

  onStartQueue.flush();
  updateQueue.flush(prevTs ? Math.min(64, ts - prevTs) : 16.667);
  onFrameQueue.flush();
  writeQueue.flush();
  onFinishQueue.flush();
}

function makeQueue() {
  var next = new Set();
  var current = next;
  return {
    add: function add(fn) {
      __raf.count += current == next && !next.has(fn) ? 1 : 0;
      next.add(fn);
    },
    delete: function _delete(fn) {
      __raf.count -= current == next && next.has(fn) ? 1 : 0;
      return next.delete(fn);
    },
    flush: function flush(arg) {
      if (current.size) {
        next = new Set();
        __raf.count -= current.size;
        eachSafely(current, function (fn) {
          return fn(arg) && next.add(fn);
        });
        __raf.count += next.size;
        current = next;
      }
    }
  };
}

function eachSafely(values, each) {
  values.forEach(function (value) {
    try {
      each(value);
    } catch (e) {
      raf.catch(e);
    }
  });
}

var __raf = {
  count: 0,
  clear: function clear() {
    ts = -1;
    timeouts = [];
    onStartQueue = makeQueue();
    updateQueue = makeQueue();
    onFrameQueue = makeQueue();
    writeQueue = makeQueue();
    onFinishQueue = makeQueue();
    __raf.count = 0;
  }
};

function noop() {}

var defineHidden = function defineHidden(obj, key, value) {
  return Object.defineProperty(obj, key, {
    value: value,
    writable: true,
    configurable: true
  });
};

var is = {
  arr: Array.isArray,
  obj: function obj(a) {
    return !!a && a.constructor.name === 'Object';
  },
  fun: function fun(a) {
    return typeof a === 'function';
  },
  str: function str(a) {
    return typeof a === 'string';
  },
  num: function num(a) {
    return typeof a === 'number';
  },
  und: function und(a) {
    return a === undefined;
  }
};

function isEqual(a, b) {
  if (is.arr(a)) {
    if (!is.arr(b) || a.length !== b.length) return false;

    for (var i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return false;
    }

    return true;
  }

  return a === b;
}

var each = function each(obj, fn) {
  return obj.forEach(fn);
};

function eachProp(obj, fn, ctx) {
  if (is.arr(obj)) {
    for (var i = 0; i < obj.length; i++) {
      fn.call(ctx, obj[i], "" + i);
    }

    return;
  }

  for (var key in obj) {
    if (obj.hasOwnProperty(key)) {
      fn.call(ctx, obj[key], key);
    }
  }
}

var toArray = function toArray(a) {
  return is.und(a) ? [] : is.arr(a) ? a : [a];
};

function flush(queue, iterator) {
  if (queue.size) {
    var items = Array.from(queue);
    queue.clear();
    each(items, iterator);
  }
}

var flushCalls = function flushCalls(queue) {
  for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    args[_key - 1] = arguments[_key];
  }

  return flush(queue, function (fn) {
    return fn.apply(void 0, args);
  });
};

var createStringInterpolator$1;
var to;
var colors$1 = null;
var skipAnimation = false;
var willAdvance = noop;

var assign = function assign(globals) {
  if (globals.to) to = globals.to;
  if (globals.now) raf.now = globals.now;
  if (globals.colors !== undefined) colors$1 = globals.colors;
  if (globals.skipAnimation != null) skipAnimation = globals.skipAnimation;
  if (globals.createStringInterpolator) createStringInterpolator$1 = globals.createStringInterpolator;
  if (globals.requestAnimationFrame) raf.use(globals.requestAnimationFrame);
  if (globals.batchedUpdates) raf.batchedUpdates = globals.batchedUpdates;
  if (globals.willAdvance) willAdvance = globals.willAdvance;
  if (globals.frameLoop) raf.frameLoop = globals.frameLoop;
};

var globals = /*#__PURE__*/Object.freeze({
  __proto__: null,

  get createStringInterpolator() {
    return createStringInterpolator$1;
  },

  get to() {
    return to;
  },

  get colors() {
    return colors$1;
  },

  get skipAnimation() {
    return skipAnimation;
  },

  get willAdvance() {
    return willAdvance;
  },

  assign: assign
});
var startQueue = /*#__PURE__*/new Set();
var currentFrame = [];
var prevFrame = [];
var priority = 0;
var frameLoop = {
  get idle() {
    return !startQueue.size && !currentFrame.length;
  },

  start: function start(animation) {
    if (priority > animation.priority) {
      startQueue.add(animation);
      raf.onStart(flushStartQueue);
    } else {
      startSafely(animation);
      raf(advance);
    }
  },
  advance: advance,
  sort: function sort(animation) {
    if (priority) {
      raf.onFrame(function () {
        return frameLoop.sort(animation);
      });
    } else {
      var prevIndex = currentFrame.indexOf(animation);

      if (~prevIndex) {
        currentFrame.splice(prevIndex, 1);
        startUnsafely(animation);
      }
    }
  },
  clear: function clear() {
    currentFrame = [];
    startQueue.clear();
  }
};

function flushStartQueue() {
  startQueue.forEach(startSafely);
  startQueue.clear();
  raf(advance);
}

function startSafely(animation) {
  if (!currentFrame.includes(animation)) startUnsafely(animation);
}

function startUnsafely(animation) {
  currentFrame.splice(findIndex(currentFrame, function (other) {
    return other.priority > animation.priority;
  }), 0, animation);
}

function advance(dt) {
  var nextFrame = prevFrame;

  for (var i = 0; i < currentFrame.length; i++) {
    var animation = currentFrame[i];
    priority = animation.priority;

    if (!animation.idle) {
      willAdvance(animation);
      animation.advance(dt);

      if (!animation.idle) {
        nextFrame.push(animation);
      }
    }
  }

  priority = 0;
  prevFrame = currentFrame;
  prevFrame.length = 0;
  currentFrame = nextFrame;
  return currentFrame.length > 0;
}

function findIndex(arr, test) {
  var index = arr.findIndex(test);
  return index < 0 ? arr.length : index;
}

var colors = {
  transparent: 0x00000000,
  aliceblue: 0xf0f8ffff,
  antiquewhite: 0xfaebd7ff,
  aqua: 0x00ffffff,
  aquamarine: 0x7fffd4ff,
  azure: 0xf0ffffff,
  beige: 0xf5f5dcff,
  bisque: 0xffe4c4ff,
  black: 0x000000ff,
  blanchedalmond: 0xffebcdff,
  blue: 0x0000ffff,
  blueviolet: 0x8a2be2ff,
  brown: 0xa52a2aff,
  burlywood: 0xdeb887ff,
  burntsienna: 0xea7e5dff,
  cadetblue: 0x5f9ea0ff,
  chartreuse: 0x7fff00ff,
  chocolate: 0xd2691eff,
  coral: 0xff7f50ff,
  cornflowerblue: 0x6495edff,
  cornsilk: 0xfff8dcff,
  crimson: 0xdc143cff,
  cyan: 0x00ffffff,
  darkblue: 0x00008bff,
  darkcyan: 0x008b8bff,
  darkgoldenrod: 0xb8860bff,
  darkgray: 0xa9a9a9ff,
  darkgreen: 0x006400ff,
  darkgrey: 0xa9a9a9ff,
  darkkhaki: 0xbdb76bff,
  darkmagenta: 0x8b008bff,
  darkolivegreen: 0x556b2fff,
  darkorange: 0xff8c00ff,
  darkorchid: 0x9932ccff,
  darkred: 0x8b0000ff,
  darksalmon: 0xe9967aff,
  darkseagreen: 0x8fbc8fff,
  darkslateblue: 0x483d8bff,
  darkslategray: 0x2f4f4fff,
  darkslategrey: 0x2f4f4fff,
  darkturquoise: 0x00ced1ff,
  darkviolet: 0x9400d3ff,
  deeppink: 0xff1493ff,
  deepskyblue: 0x00bfffff,
  dimgray: 0x696969ff,
  dimgrey: 0x696969ff,
  dodgerblue: 0x1e90ffff,
  firebrick: 0xb22222ff,
  floralwhite: 0xfffaf0ff,
  forestgreen: 0x228b22ff,
  fuchsia: 0xff00ffff,
  gainsboro: 0xdcdcdcff,
  ghostwhite: 0xf8f8ffff,
  gold: 0xffd700ff,
  goldenrod: 0xdaa520ff,
  gray: 0x808080ff,
  green: 0x008000ff,
  greenyellow: 0xadff2fff,
  grey: 0x808080ff,
  honeydew: 0xf0fff0ff,
  hotpink: 0xff69b4ff,
  indianred: 0xcd5c5cff,
  indigo: 0x4b0082ff,
  ivory: 0xfffff0ff,
  khaki: 0xf0e68cff,
  lavender: 0xe6e6faff,
  lavenderblush: 0xfff0f5ff,
  lawngreen: 0x7cfc00ff,
  lemonchiffon: 0xfffacdff,
  lightblue: 0xadd8e6ff,
  lightcoral: 0xf08080ff,
  lightcyan: 0xe0ffffff,
  lightgoldenrodyellow: 0xfafad2ff,
  lightgray: 0xd3d3d3ff,
  lightgreen: 0x90ee90ff,
  lightgrey: 0xd3d3d3ff,
  lightpink: 0xffb6c1ff,
  lightsalmon: 0xffa07aff,
  lightseagreen: 0x20b2aaff,
  lightskyblue: 0x87cefaff,
  lightslategray: 0x778899ff,
  lightslategrey: 0x778899ff,
  lightsteelblue: 0xb0c4deff,
  lightyellow: 0xffffe0ff,
  lime: 0x00ff00ff,
  limegreen: 0x32cd32ff,
  linen: 0xfaf0e6ff,
  magenta: 0xff00ffff,
  maroon: 0x800000ff,
  mediumaquamarine: 0x66cdaaff,
  mediumblue: 0x0000cdff,
  mediumorchid: 0xba55d3ff,
  mediumpurple: 0x9370dbff,
  mediumseagreen: 0x3cb371ff,
  mediumslateblue: 0x7b68eeff,
  mediumspringgreen: 0x00fa9aff,
  mediumturquoise: 0x48d1ccff,
  mediumvioletred: 0xc71585ff,
  midnightblue: 0x191970ff,
  mintcream: 0xf5fffaff,
  mistyrose: 0xffe4e1ff,
  moccasin: 0xffe4b5ff,
  navajowhite: 0xffdeadff,
  navy: 0x000080ff,
  oldlace: 0xfdf5e6ff,
  olive: 0x808000ff,
  olivedrab: 0x6b8e23ff,
  orange: 0xffa500ff,
  orangered: 0xff4500ff,
  orchid: 0xda70d6ff,
  palegoldenrod: 0xeee8aaff,
  palegreen: 0x98fb98ff,
  paleturquoise: 0xafeeeeff,
  palevioletred: 0xdb7093ff,
  papayawhip: 0xffefd5ff,
  peachpuff: 0xffdab9ff,
  peru: 0xcd853fff,
  pink: 0xffc0cbff,
  plum: 0xdda0ddff,
  powderblue: 0xb0e0e6ff,
  purple: 0x800080ff,
  rebeccapurple: 0x663399ff,
  red: 0xff0000ff,
  rosybrown: 0xbc8f8fff,
  royalblue: 0x4169e1ff,
  saddlebrown: 0x8b4513ff,
  salmon: 0xfa8072ff,
  sandybrown: 0xf4a460ff,
  seagreen: 0x2e8b57ff,
  seashell: 0xfff5eeff,
  sienna: 0xa0522dff,
  silver: 0xc0c0c0ff,
  skyblue: 0x87ceebff,
  slateblue: 0x6a5acdff,
  slategray: 0x708090ff,
  slategrey: 0x708090ff,
  snow: 0xfffafaff,
  springgreen: 0x00ff7fff,
  steelblue: 0x4682b4ff,
  tan: 0xd2b48cff,
  teal: 0x008080ff,
  thistle: 0xd8bfd8ff,
  tomato: 0xff6347ff,
  turquoise: 0x40e0d0ff,
  violet: 0xee82eeff,
  wheat: 0xf5deb3ff,
  white: 0xffffffff,
  whitesmoke: 0xf5f5f5ff,
  yellow: 0xffff00ff,
  yellowgreen: 0x9acd32ff
};
var NUMBER = '[-+]?\\d*\\.?\\d+';
var PERCENTAGE = NUMBER + '%';

function call() {
  for (var _len2 = arguments.length, parts = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
    parts[_key2] = arguments[_key2];
  }

  return '\\(\\s*(' + parts.join(')\\s*,\\s*(') + ')\\s*\\)';
}

var rgb = /*#__PURE__*/new RegExp('rgb' + /*#__PURE__*/call(NUMBER, NUMBER, NUMBER));
var rgba = /*#__PURE__*/new RegExp('rgba' + /*#__PURE__*/call(NUMBER, NUMBER, NUMBER, NUMBER));
var hsl = /*#__PURE__*/new RegExp('hsl' + /*#__PURE__*/call(NUMBER, PERCENTAGE, PERCENTAGE));
var hsla = /*#__PURE__*/new RegExp('hsla' + /*#__PURE__*/call(NUMBER, PERCENTAGE, PERCENTAGE, NUMBER));
var hex3 = /^#([0-9a-fA-F]{1})([0-9a-fA-F]{1})([0-9a-fA-F]{1})$/;
var hex4 = /^#([0-9a-fA-F]{1})([0-9a-fA-F]{1})([0-9a-fA-F]{1})([0-9a-fA-F]{1})$/;
var hex6 = /^#([0-9a-fA-F]{6})$/;
var hex8 = /^#([0-9a-fA-F]{8})$/;

function normalizeColor(color) {
  var match;

  if (typeof color === 'number') {
    return color >>> 0 === color && color >= 0 && color <= 0xffffffff ? color : null;
  }

  if (match = hex6.exec(color)) return parseInt(match[1] + 'ff', 16) >>> 0;

  if (colors$1 && colors$1[color] !== undefined) {
    return colors$1[color];
  }

  if (match = rgb.exec(color)) {
    return (parse255(match[1]) << 24 | parse255(match[2]) << 16 | parse255(match[3]) << 8 | 0x000000ff) >>> 0;
  }

  if (match = rgba.exec(color)) {
    return (parse255(match[1]) << 24 | parse255(match[2]) << 16 | parse255(match[3]) << 8 | parse1(match[4])) >>> 0;
  }

  if (match = hex3.exec(color)) {
    return parseInt(match[1] + match[1] + match[2] + match[2] + match[3] + match[3] + 'ff', 16) >>> 0;
  }

  if (match = hex8.exec(color)) return parseInt(match[1], 16) >>> 0;

  if (match = hex4.exec(color)) {
    return parseInt(match[1] + match[1] + match[2] + match[2] + match[3] + match[3] + match[4] + match[4], 16) >>> 0;
  }

  if (match = hsl.exec(color)) {
    return (hslToRgb(parse360(match[1]), parsePercentage(match[2]), parsePercentage(match[3])) | 0x000000ff) >>> 0;
  }

  if (match = hsla.exec(color)) {
    return (hslToRgb(parse360(match[1]), parsePercentage(match[2]), parsePercentage(match[3])) | parse1(match[4])) >>> 0;
  }

  return null;
}

function hue2rgb(p, q, t) {
  if (t < 0) t += 1;
  if (t > 1) t -= 1;
  if (t < 1 / 6) return p + (q - p) * 6 * t;
  if (t < 1 / 2) return q;
  if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
  return p;
}

function hslToRgb(h, s, l) {
  var q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  var p = 2 * l - q;
  var r = hue2rgb(p, q, h + 1 / 3);
  var g = hue2rgb(p, q, h);
  var b = hue2rgb(p, q, h - 1 / 3);
  return Math.round(r * 255) << 24 | Math.round(g * 255) << 16 | Math.round(b * 255) << 8;
}

function parse255(str) {
  var int = parseInt(str, 10);
  if (int < 0) return 0;
  if (int > 255) return 255;
  return int;
}

function parse360(str) {
  var int = parseFloat(str);
  return (int % 360 + 360) % 360 / 360;
}

function parse1(str) {
  var num = parseFloat(str);
  if (num < 0) return 0;
  if (num > 1) return 255;
  return Math.round(num * 255);
}

function parsePercentage(str) {
  var int = parseFloat(str);
  if (int < 0) return 0;
  if (int > 100) return 1;
  return int / 100;
}

function colorToRgba(input) {
  var int32Color = normalizeColor(input);
  if (int32Color === null) return input;
  int32Color = int32Color || 0;
  var r = (int32Color & 0xff000000) >>> 24;
  var g = (int32Color & 0x00ff0000) >>> 16;
  var b = (int32Color & 0x0000ff00) >>> 8;
  var a = (int32Color & 0x000000ff) / 255;
  return "rgba(" + r + ", " + g + ", " + b + ", " + a + ")";
}

var createInterpolator = function createInterpolator(range, output, extrapolate) {
  if (is.fun(range)) {
    return range;
  }

  if (is.arr(range)) {
    return createInterpolator({
      range: range,
      output: output,
      extrapolate: extrapolate
    });
  }

  if (is.str(range.output[0])) {
    return createStringInterpolator$1(range);
  }

  var config = range;
  var outputRange = config.output;
  var inputRange = config.range || [0, 1];
  var extrapolateLeft = config.extrapolateLeft || config.extrapolate || 'extend';
  var extrapolateRight = config.extrapolateRight || config.extrapolate || 'extend';

  var easing = config.easing || function (t) {
    return t;
  };

  return function (input) {
    var range = findRange(input, inputRange);
    return interpolate(input, inputRange[range], inputRange[range + 1], outputRange[range], outputRange[range + 1], easing, extrapolateLeft, extrapolateRight, config.map);
  };
};

function interpolate(input, inputMin, inputMax, outputMin, outputMax, easing, extrapolateLeft, extrapolateRight, map) {
  var result = map ? map(input) : input;

  if (result < inputMin) {
    if (extrapolateLeft === 'identity') return result;else if (extrapolateLeft === 'clamp') result = inputMin;
  }

  if (result > inputMax) {
    if (extrapolateRight === 'identity') return result;else if (extrapolateRight === 'clamp') result = inputMax;
  }

  if (outputMin === outputMax) return outputMin;
  if (inputMin === inputMax) return input <= inputMin ? outputMin : outputMax;
  if (inputMin === -Infinity) result = -result;else if (inputMax === Infinity) result = result - inputMin;else result = (result - inputMin) / (inputMax - inputMin);
  result = easing(result);
  if (outputMin === -Infinity) result = -result;else if (outputMax === Infinity) result = result + outputMin;else result = result * (outputMax - outputMin) + outputMin;
  return result;
}

function findRange(input, inputRange) {
  for (var i = 1; i < inputRange.length - 1; ++i) {
    if (inputRange[i] >= input) break;
  }

  return i - 1;
}

function _extends$2() {
  _extends$2 = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$2.apply(this, arguments);
}

var $get = /*#__PURE__*/Symbol.for('FluidValue.get');
var $observers = /*#__PURE__*/Symbol.for('FluidValue.observers');

var hasFluidValue = function hasFluidValue(arg) {
  return Boolean(arg && arg[$get]);
};

var getFluidValue = function getFluidValue(arg) {
  return arg && arg[$get] ? arg[$get]() : arg;
};

var getFluidObservers = function getFluidObservers(target) {
  return target[$observers] || null;
};

function callFluidObserver(observer, event) {
  if (observer.eventObserved) {
    observer.eventObserved(event);
  } else {
    observer(event);
  }
}

function callFluidObservers(target, event) {
  var observers = target[$observers];

  if (observers) {
    observers.forEach(function (observer) {
      callFluidObserver(observer, event);
    });
  }
}

var FluidValue = function FluidValue(get) {
  this[$get] = void 0;
  this[$observers] = void 0;

  if (!get && !(get = this.get)) {
    throw Error('Unknown getter');
  }

  setFluidGetter(this, get);
};

var setFluidGetter = function setFluidGetter(target, get) {
  return setHidden(target, $get, get);
};

function addFluidObserver(target, observer) {
  if (target[$get]) {
    var observers = target[$observers];

    if (!observers) {
      setHidden(target, $observers, observers = new Set());
    }

    if (!observers.has(observer)) {
      observers.add(observer);

      if (target.observerAdded) {
        target.observerAdded(observers.size, observer);
      }
    }
  }

  return observer;
}

function removeFluidObserver(target, observer) {
  var observers = target[$observers];

  if (observers && observers.has(observer)) {
    var count = observers.size - 1;

    if (count) {
      observers.delete(observer);
    } else {
      target[$observers] = null;
    }

    if (target.observerRemoved) {
      target.observerRemoved(count, observer);
    }
  }
}

var setHidden = function setHidden(target, key, value) {
  return Object.defineProperty(target, key, {
    value: value,
    writable: true,
    configurable: true
  });
};

var numberRegex = /[+\-]?(?:0|[1-9]\d*)(?:\.\d*)?(?:[eE][+\-]?\d+)?/g;
var colorRegex = /(#(?:[0-9a-f]{2}){2,4}|(#[0-9a-f]{3})|(rgb|hsl)a?\((-?\d+%?[,\s]+){2,3}\s*[\d\.]+%?\))/gi;
var unitRegex = /*#__PURE__*/new RegExp("(" + numberRegex.source + ")(%|[a-z]+)", 'i');
var namedColorRegex;
var rgbaRegex = /rgba\(([0-9\.-]+), ([0-9\.-]+), ([0-9\.-]+), ([0-9\.-]+)\)/gi;

var rgbaRound = function rgbaRound(_, p1, p2, p3, p4) {
  return "rgba(" + Math.round(p1) + ", " + Math.round(p2) + ", " + Math.round(p3) + ", " + p4 + ")";
};

var createStringInterpolator = function createStringInterpolator(config) {
  if (!namedColorRegex) namedColorRegex = colors$1 ? new RegExp("(" + Object.keys(colors$1).join('|') + ")(?!\\w)", 'g') : /^\b$/;
  var output = config.output.map(function (value) {
    return getFluidValue(value).replace(colorRegex, colorToRgba).replace(namedColorRegex, colorToRgba);
  });
  var keyframes = output.map(function (value) {
    return value.match(numberRegex).map(Number);
  });
  var outputRanges = keyframes[0].map(function (_, i) {
    return keyframes.map(function (values) {
      if (!(i in values)) {
        throw Error('The arity of each "output" value must be equal');
      }

      return values[i];
    });
  });
  var interpolators = outputRanges.map(function (output) {
    return createInterpolator(_extends$2({}, config, {
      output: output
    }));
  });
  return function (input) {
    var _output$find;

    var missingUnit = !unitRegex.test(output[0]) && ((_output$find = output.find(function (value) {
      return unitRegex.test(value);
    })) == null ? void 0 : _output$find.replace(numberRegex, ''));
    var i = 0;
    return output[0].replace(numberRegex, function () {
      return "" + interpolators[i++](input) + (missingUnit || '');
    }).replace(rgbaRegex, rgbaRound);
  };
};

var prefix = 'react-spring: ';

var once = function once(fn) {
  var func = fn;
  var called = false;

  if (typeof func != 'function') {
    throw new TypeError(prefix + "once requires a function parameter");
  }

  return function () {
    if (!called) {
      func.apply(void 0, arguments);
      called = true;
    }
  };
};

var warnInterpolate = /*#__PURE__*/once(console.warn);

function deprecateInterpolate() {
  warnInterpolate(prefix + "The \"interpolate\" function is deprecated in v9 (use \"to\" instead)");
}

var warnDirectCall = /*#__PURE__*/once(console.warn);

function deprecateDirectCall() {
  warnDirectCall(prefix + "Directly calling start instead of using the api object is deprecated in v9 (use \".start\" instead), this will be removed in later 0.X.0 versions");
}

function isAnimatedString(value) {
  return is.str(value) && (value[0] == '#' || /\d/.test(value) || value in (colors$1 || {}));
}

var useOnce = function useOnce(effect) {
  return useEffect(effect, emptyDeps);
};

var emptyDeps = [];

function useForceUpdate() {
  var update = useState()[1];
  var mounted = useState(makeMountedRef)[0];
  useOnce(mounted.unmount);
  return function () {
    if (mounted.current) {
      update({});
    }
  };
}

function makeMountedRef() {
  var mounted = {
    current: true,
    unmount: function unmount() {
      return function () {
        mounted.current = false;
      };
    }
  };
  return mounted;
}

function useMemoOne(getResult, inputs) {
  var _useState = useState(function () {
    return {
      inputs: inputs,
      result: getResult()
    };
  }),
      initial = _useState[0];

  var committed = useRef();
  var prevCache = committed.current;
  var cache = prevCache;

  if (cache) {
    var useCache = Boolean(inputs && cache.inputs && areInputsEqual(inputs, cache.inputs));

    if (!useCache) {
      cache = {
        inputs: inputs,
        result: getResult()
      };
    }
  } else {
    cache = initial;
  }

  useEffect(function () {
    committed.current = cache;

    if (prevCache == initial) {
      initial.inputs = initial.result = undefined;
    }
  }, [cache]);
  return cache.result;
}

function areInputsEqual(next, prev) {
  if (next.length !== prev.length) {
    return false;
  }

  for (var i = 0; i < next.length; i++) {
    if (next[i] !== prev[i]) {
      return false;
    }
  }

  return true;
}

function usePrev(value) {
  var prevRef = useRef();
  useEffect(function () {
    prevRef.current = value;
  });
  return prevRef.current;
}

var useLayoutEffect = typeof window !== 'undefined' && window.document && window.document.createElement ? React.useLayoutEffect : React.useEffect;

var $node = /*#__PURE__*/Symbol.for('Animated:node');

var isAnimated = function isAnimated(value) {
  return !!value && value[$node] === value;
};

var getAnimated = function getAnimated(owner) {
  return owner && owner[$node];
};

var setAnimated = function setAnimated(owner, node) {
  return defineHidden(owner, $node, node);
};

var getPayload = function getPayload(owner) {
  return owner && owner[$node] && owner[$node].getPayload();
};

var Animated = /*#__PURE__*/function () {
  function Animated() {
    this.payload = void 0;
    setAnimated(this, this);
  }

  var _proto = Animated.prototype;

  _proto.getPayload = function getPayload() {
    return this.payload || [];
  };

  return Animated;
}();

var AnimatedValue = /*#__PURE__*/function (_Animated) {
  _inheritsLoose(AnimatedValue, _Animated);

  function AnimatedValue(_value) {
    var _this;

    _this = _Animated.call(this) || this;
    _this.done = true;
    _this.elapsedTime = void 0;
    _this.lastPosition = void 0;
    _this.lastVelocity = void 0;
    _this.v0 = void 0;
    _this.durationProgress = 0;
    _this._value = _value;

    if (is.num(_this._value)) {
      _this.lastPosition = _this._value;
    }

    return _this;
  }

  AnimatedValue.create = function create(value) {
    return new AnimatedValue(value);
  };

  var _proto2 = AnimatedValue.prototype;

  _proto2.getPayload = function getPayload() {
    return [this];
  };

  _proto2.getValue = function getValue() {
    return this._value;
  };

  _proto2.setValue = function setValue(value, step) {
    if (is.num(value)) {
      this.lastPosition = value;

      if (step) {
        value = Math.round(value / step) * step;

        if (this.done) {
          this.lastPosition = value;
        }
      }
    }

    if (this._value === value) {
      return false;
    }

    this._value = value;
    return true;
  };

  _proto2.reset = function reset() {
    var done = this.done;
    this.done = false;

    if (is.num(this._value)) {
      this.elapsedTime = 0;
      this.durationProgress = 0;
      this.lastPosition = this._value;
      if (done) this.lastVelocity = null;
      this.v0 = null;
    }
  };

  return AnimatedValue;
}(Animated);

var AnimatedString = /*#__PURE__*/function (_AnimatedValue) {
  _inheritsLoose(AnimatedString, _AnimatedValue);

  function AnimatedString(value) {
    var _this2;

    _this2 = _AnimatedValue.call(this, 0) || this;
    _this2._string = null;
    _this2._toString = void 0;
    _this2._toString = createInterpolator({
      output: [value, value]
    });
    return _this2;
  }

  AnimatedString.create = function create(value) {
    return new AnimatedString(value);
  };

  var _proto3 = AnimatedString.prototype;

  _proto3.getValue = function getValue() {
    var value = this._string;
    return value == null ? this._string = this._toString(this._value) : value;
  };

  _proto3.setValue = function setValue(value) {
    if (is.str(value)) {
      if (value == this._string) {
        return false;
      }

      this._string = value;
      this._value = 1;
    } else if (_AnimatedValue.prototype.setValue.call(this, value)) {
      this._string = null;
    } else {
      return false;
    }

    return true;
  };

  _proto3.reset = function reset(goal) {
    if (goal) {
      this._toString = createInterpolator({
        output: [this.getValue(), goal]
      });
    }

    this._value = 0;

    _AnimatedValue.prototype.reset.call(this);
  };

  return AnimatedString;
}(AnimatedValue);

var TreeContext = {
  dependencies: null
};

var AnimatedObject = /*#__PURE__*/function (_Animated2) {
  _inheritsLoose(AnimatedObject, _Animated2);

  function AnimatedObject(source) {
    var _this3;

    _this3 = _Animated2.call(this) || this;
    _this3.source = source;

    _this3.setValue(source);

    return _this3;
  }

  var _proto4 = AnimatedObject.prototype;

  _proto4.getValue = function getValue(animated) {
    var values = {};
    eachProp(this.source, function (source, key) {
      if (isAnimated(source)) {
        values[key] = source.getValue(animated);
      } else if (hasFluidValue(source)) {
        values[key] = getFluidValue(source);
      } else if (!animated) {
        values[key] = source;
      }
    });
    return values;
  };

  _proto4.setValue = function setValue(source) {
    this.source = source;
    this.payload = this._makePayload(source);
  };

  _proto4.reset = function reset() {
    if (this.payload) {
      each(this.payload, function (node) {
        return node.reset();
      });
    }
  };

  _proto4._makePayload = function _makePayload(source) {
    if (source) {
      var payload = new Set();
      eachProp(source, this._addToPayload, payload);
      return Array.from(payload);
    }
  };

  _proto4._addToPayload = function _addToPayload(source) {
    var _this4 = this;

    if (TreeContext.dependencies && hasFluidValue(source)) {
      TreeContext.dependencies.add(source);
    }

    var payload = getPayload(source);

    if (payload) {
      each(payload, function (node) {
        return _this4.add(node);
      });
    }
  };

  return AnimatedObject;
}(Animated);

var AnimatedArray = /*#__PURE__*/function (_AnimatedObject) {
  _inheritsLoose(AnimatedArray, _AnimatedObject);

  function AnimatedArray(source) {
    return _AnimatedObject.call(this, source) || this;
  }

  AnimatedArray.create = function create(source) {
    return new AnimatedArray(source);
  };

  var _proto5 = AnimatedArray.prototype;

  _proto5.getValue = function getValue() {
    return this.source.map(function (node) {
      return node.getValue();
    });
  };

  _proto5.setValue = function setValue(source) {
    var payload = this.getPayload();

    if (source.length == payload.length) {
      return payload.map(function (node, i) {
        return node.setValue(source[i]);
      }).some(Boolean);
    }

    _AnimatedObject.prototype.setValue.call(this, source.map(makeAnimated));

    return true;
  };

  return AnimatedArray;
}(AnimatedObject);

function makeAnimated(value) {
  var nodeType = isAnimatedString(value) ? AnimatedString : AnimatedValue;
  return nodeType.create(value);
}

function getAnimatedType(value) {
  var parentNode = getAnimated(value);
  return parentNode ? parentNode.constructor : is.arr(value) ? AnimatedArray : isAnimatedString(value) ? AnimatedString : AnimatedValue;
}

function _extends$1() {
  _extends$1 = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$1.apply(this, arguments);
}

var withAnimated = function withAnimated(Component, host) {
  var hasInstance = !is.fun(Component) || Component.prototype && Component.prototype.isReactComponent;
  return /*#__PURE__*/forwardRef(function (givenProps, givenRef) {
    var instanceRef = useRef(null);
    var ref = hasInstance && useCallback(function (value) {
      instanceRef.current = updateRef(givenRef, value);
    }, [givenRef]);

    var _getAnimatedState = getAnimatedState(givenProps, host),
        props = _getAnimatedState[0],
        deps = _getAnimatedState[1];

    var forceUpdate = useForceUpdate();

    var callback = function callback() {
      var instance = instanceRef.current;

      if (hasInstance && !instance) {
        return;
      }

      var didUpdate = instance ? host.applyAnimatedValues(instance, props.getValue(true)) : false;

      if (didUpdate === false) {
        forceUpdate();
      }
    };

    var observer = new PropsObserver(callback, deps);
    var observerRef = useRef();
    useLayoutEffect(function () {
      var lastObserver = observerRef.current;
      observerRef.current = observer;
      each(deps, function (dep) {
        return addFluidObserver(dep, observer);
      });

      if (lastObserver) {
        each(lastObserver.deps, function (dep) {
          return removeFluidObserver(dep, lastObserver);
        });
        raf.cancel(lastObserver.update);
      }
    });
    useEffect(callback, []);
    useOnce(function () {
      return function () {
        var observer = observerRef.current;
        each(observer.deps, function (dep) {
          return removeFluidObserver(dep, observer);
        });
      };
    });
    var usedProps = host.getComponentProps(props.getValue());
    return /*#__PURE__*/React.createElement(Component, _extends$1({}, usedProps, {
      ref: ref
    }));
  });
};

var PropsObserver = /*#__PURE__*/function () {
  function PropsObserver(update, deps) {
    this.update = update;
    this.deps = deps;
  }

  var _proto6 = PropsObserver.prototype;

  _proto6.eventObserved = function eventObserved(event) {
    if (event.type == 'change') {
      raf.write(this.update);
    }
  };

  return PropsObserver;
}();

function getAnimatedState(props, host) {
  var dependencies = new Set();
  TreeContext.dependencies = dependencies;
  if (props.style) props = _extends$1({}, props, {
    style: host.createAnimatedStyle(props.style)
  });
  props = new AnimatedObject(props);
  TreeContext.dependencies = null;
  return [props, dependencies];
}

function updateRef(ref, value) {
  if (ref) {
    if (is.fun(ref)) ref(value);else ref.current = value;
  }

  return value;
}

var cacheKey = /*#__PURE__*/Symbol.for('AnimatedComponent');

var createHost = function createHost(components, _temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      _ref$applyAnimatedVal = _ref.applyAnimatedValues,
      _applyAnimatedValues = _ref$applyAnimatedVal === void 0 ? function () {
    return false;
  } : _ref$applyAnimatedVal,
      _ref$createAnimatedSt = _ref.createAnimatedStyle,
      _createAnimatedStyle = _ref$createAnimatedSt === void 0 ? function (style) {
    return new AnimatedObject(style);
  } : _ref$createAnimatedSt,
      _ref$getComponentProp = _ref.getComponentProps,
      _getComponentProps = _ref$getComponentProp === void 0 ? function (props) {
    return props;
  } : _ref$getComponentProp;

  var hostConfig = {
    applyAnimatedValues: _applyAnimatedValues,
    createAnimatedStyle: _createAnimatedStyle,
    getComponentProps: _getComponentProps
  };

  var animated = function animated(Component) {
    var displayName = getDisplayName(Component) || 'Anonymous';

    if (is.str(Component)) {
      Component = animated[Component] || (animated[Component] = withAnimated(Component, hostConfig));
    } else {
      Component = Component[cacheKey] || (Component[cacheKey] = withAnimated(Component, hostConfig));
    }

    Component.displayName = "Animated(" + displayName + ")";
    return Component;
  };

  eachProp(components, function (Component, key) {
    if (is.arr(components)) {
      key = getDisplayName(Component);
    }

    animated[key] = animated(Component);
  });
  return {
    animated: animated
  };
};

var getDisplayName = function getDisplayName(arg) {
  return is.str(arg) ? arg : arg && is.str(arg.displayName) ? arg.displayName : is.fun(arg) && arg.name || null;
};

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function callProp(value) {
  for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    args[_key - 1] = arguments[_key];
  }

  return is.fun(value) ? value.apply(void 0, args) : value;
}

var matchProp = function matchProp(value, key) {
  return value === true || !!(key && value && (is.fun(value) ? value(key) : toArray(value).includes(key)));
};

var resolveProp = function resolveProp(prop, key) {
  return is.obj(prop) ? key && prop[key] : prop;
};

var getDefaultProp = function getDefaultProp(props, key) {
  return props.default === true ? props[key] : props.default ? props.default[key] : undefined;
};

var noopTransform = function noopTransform(value) {
  return value;
};

var getDefaultProps = function getDefaultProps(props, transform) {
  if (transform === void 0) {
    transform = noopTransform;
  }

  var keys = DEFAULT_PROPS;

  if (props.default && props.default !== true) {
    props = props.default;
    keys = Object.keys(props);
  }

  var defaults = {};

  for (var _iterator = _createForOfIteratorHelperLoose(keys), _step; !(_step = _iterator()).done;) {
    var key = _step.value;
    var value = transform(props[key], key);

    if (!is.und(value)) {
      defaults[key] = value;
    }
  }

  return defaults;
};

var DEFAULT_PROPS = ['config', 'onProps', 'onStart', 'onChange', 'onPause', 'onResume', 'onRest'];
var RESERVED_PROPS = {
  config: 1,
  from: 1,
  to: 1,
  ref: 1,
  loop: 1,
  reset: 1,
  pause: 1,
  cancel: 1,
  reverse: 1,
  immediate: 1,
  default: 1,
  delay: 1,
  onProps: 1,
  onStart: 1,
  onChange: 1,
  onPause: 1,
  onResume: 1,
  onRest: 1,
  onResolve: 1,
  items: 1,
  trail: 1,
  sort: 1,
  expires: 1,
  initial: 1,
  enter: 1,
  update: 1,
  leave: 1,
  children: 1,
  onDestroyed: 1,
  keys: 1,
  callId: 1,
  parentId: 1
};

function getForwardProps(props) {
  var forward = {};
  var count = 0;
  eachProp(props, function (value, prop) {
    if (!RESERVED_PROPS[prop]) {
      forward[prop] = value;
      count++;
    }
  });

  if (count) {
    return forward;
  }
}

function inferTo(props) {
  var to = getForwardProps(props);

  if (to) {
    var out = {
      to: to
    };
    eachProp(props, function (val, key) {
      return key in to || (out[key] = val);
    });
    return out;
  }

  return _extends({}, props);
}

function computeGoal(value) {
  value = getFluidValue(value);
  return is.arr(value) ? value.map(computeGoal) : isAnimatedString(value) ? globals.createStringInterpolator({
    range: [0, 1],
    output: [value, value]
  })(1) : value;
}

function hasProps(props) {
  for (var _ in props) {
    return true;
  }

  return false;
}

function isAsyncTo(to) {
  return is.fun(to) || is.arr(to) && is.obj(to[0]);
}

function detachRefs(ctrl, ref) {
  var _ctrl$ref;

  (_ctrl$ref = ctrl.ref) == null ? void 0 : _ctrl$ref.delete(ctrl);
  ref == null ? void 0 : ref.delete(ctrl);
}

function replaceRef(ctrl, ref) {
  if (ref && ctrl.ref !== ref) {
    var _ctrl$ref2;

    (_ctrl$ref2 = ctrl.ref) == null ? void 0 : _ctrl$ref2.delete(ctrl);
    ref.add(ctrl);
    ctrl.ref = ref;
  }
}

var config = {
  default: {
    tension: 170,
    friction: 26
  },
  gentle: {
    tension: 120,
    friction: 14
  },
  wobbly: {
    tension: 180,
    friction: 12
  },
  stiff: {
    tension: 210,
    friction: 20
  },
  slow: {
    tension: 280,
    friction: 60
  },
  molasses: {
    tension: 280,
    friction: 120
  }
};

var linear = function linear(t) {
  return t;
};

var defaults = /*#__PURE__*/_extends({}, config.default, {
  mass: 1,
  damping: 1,
  easing: linear,
  clamp: false
});

var AnimationConfig = function AnimationConfig() {
  this.tension = void 0;
  this.friction = void 0;
  this.frequency = void 0;
  this.damping = void 0;
  this.mass = void 0;
  this.velocity = 0;
  this.restVelocity = void 0;
  this.precision = void 0;
  this.progress = void 0;
  this.duration = void 0;
  this.easing = void 0;
  this.clamp = void 0;
  this.bounce = void 0;
  this.decay = void 0;
  this.round = void 0;
  Object.assign(this, defaults);
};

function mergeConfig(config, newConfig, defaultConfig) {
  if (defaultConfig) {
    defaultConfig = _extends({}, defaultConfig);
    sanitizeConfig(defaultConfig, newConfig);
    newConfig = _extends({}, defaultConfig, newConfig);
  }

  sanitizeConfig(config, newConfig);
  Object.assign(config, newConfig);

  for (var key in defaults) {
    if (config[key] == null) {
      config[key] = defaults[key];
    }
  }

  var mass = config.mass,
      frequency = config.frequency,
      damping = config.damping;

  if (!is.und(frequency)) {
    if (frequency < 0.01) frequency = 0.01;
    if (damping < 0) damping = 0;
    config.tension = Math.pow(2 * Math.PI / frequency, 2) * mass;
    config.friction = 4 * Math.PI * damping * mass / frequency;
  }

  return config;
}

function sanitizeConfig(config, props) {
  if (!is.und(props.decay)) {
    config.duration = undefined;
  } else {
    var isTensionConfig = !is.und(props.tension) || !is.und(props.friction);

    if (isTensionConfig || !is.und(props.frequency) || !is.und(props.damping) || !is.und(props.mass)) {
      config.duration = undefined;
      config.decay = undefined;
    }

    if (isTensionConfig) {
      config.frequency = undefined;
    }
  }
}

var emptyArray = [];

var Animation = function Animation() {
  this.changed = false;
  this.values = emptyArray;
  this.toValues = null;
  this.fromValues = emptyArray;
  this.to = void 0;
  this.from = void 0;
  this.config = new AnimationConfig();
  this.immediate = false;
};

function scheduleProps(callId, _ref2) {
  var key = _ref2.key,
      props = _ref2.props,
      defaultProps = _ref2.defaultProps,
      state = _ref2.state,
      actions = _ref2.actions;
  return new Promise(function (resolve, reject) {
    var _props$cancel;

    var delay;
    var timeout;
    var cancel = matchProp((_props$cancel = props.cancel) != null ? _props$cancel : defaultProps == null ? void 0 : defaultProps.cancel, key);

    if (cancel) {
      onStart();
    } else {
      if (!is.und(props.pause)) {
        state.paused = matchProp(props.pause, key);
      }

      var pause = defaultProps == null ? void 0 : defaultProps.pause;

      if (pause !== true) {
        pause = state.paused || matchProp(pause, key);
      }

      delay = callProp(props.delay || 0, key);

      if (pause) {
        state.resumeQueue.add(onResume);
        actions.pause();
      } else {
        actions.resume();
        onResume();
      }
    }

    function onPause() {
      state.resumeQueue.add(onResume);
      state.timeouts.delete(timeout);
      timeout.cancel();
      delay = timeout.time - raf.now();
    }

    function onResume() {
      if (delay > 0) {
        timeout = raf.setTimeout(onStart, delay);
        state.pauseQueue.add(onPause);
        state.timeouts.add(timeout);
      } else {
        onStart();
      }
    }

    function onStart() {
      state.pauseQueue.delete(onPause);
      state.timeouts.delete(timeout);

      if (callId <= (state.cancelId || 0)) {
        cancel = true;
      }

      try {
        actions.start(_extends({}, props, {
          callId: callId,
          cancel: cancel
        }), resolve);
      } catch (err) {
        reject(err);
      }
    }
  });
}

var getCombinedResult = function getCombinedResult(target, results) {
  return results.length == 1 ? results[0] : results.some(function (result) {
    return result.cancelled;
  }) ? getCancelledResult(target.get()) : results.every(function (result) {
    return result.noop;
  }) ? getNoopResult(target.get()) : getFinishedResult(target.get(), results.every(function (result) {
    return result.finished;
  }));
};

var getNoopResult = function getNoopResult(value) {
  return {
    value: value,
    noop: true,
    finished: true,
    cancelled: false
  };
};

var getFinishedResult = function getFinishedResult(value, finished, cancelled) {
  if (cancelled === void 0) {
    cancelled = false;
  }

  return {
    value: value,
    finished: finished,
    cancelled: cancelled
  };
};

var getCancelledResult = function getCancelledResult(value) {
  return {
    value: value,
    cancelled: true,
    finished: false
  };
};

function runAsync(to, props, state, target) {
  var callId = props.callId,
      parentId = props.parentId,
      onRest = props.onRest;
  var prevTo = state.asyncTo,
      prevPromise = state.promise;

  if (!parentId && to === prevTo && !props.reset) {
    return prevPromise;
  }

  return state.promise = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3() {
    var defaultProps, preventBail, bail, bailPromise, bailIfEnded, animate, result, animating;
    return _regeneratorRuntime.wrap(function _callee3$(_context3) {
      while (1) {
        switch (_context3.prev = _context3.next) {
          case 0:
            state.asyncId = callId;
            state.asyncTo = to;
            defaultProps = getDefaultProps(props, function (value, key) {
              return key === 'onRest' ? undefined : value;
            });
            bailPromise = new Promise(function (resolve, reject) {
              return preventBail = resolve, bail = reject;
            });

            bailIfEnded = function bailIfEnded(bailSignal) {
              var bailResult = callId <= (state.cancelId || 0) && getCancelledResult(target) || callId !== state.asyncId && getFinishedResult(target, false);

              if (bailResult) {
                bailSignal.result = bailResult;
                bail(bailSignal);
                throw bailSignal;
              }
            };

            animate = function animate(arg1, arg2) {
              var bailSignal = new BailSignal();
              var skipAnimationSignal = new SkipAniamtionSignal();
              return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
                var props, result;
                return _regeneratorRuntime.wrap(function _callee$(_context) {
                  while (1) {
                    switch (_context.prev = _context.next) {
                      case 0:
                        if (!globals.skipAnimation) {
                          _context.next = 5;
                          break;
                        }

                        stopAsync(state);
                        skipAnimationSignal.result = getFinishedResult(target, false);
                        bail(skipAnimationSignal);
                        throw skipAnimationSignal;

                      case 5:
                        bailIfEnded(bailSignal);
                        props = is.obj(arg1) ? _extends({}, arg1) : _extends({}, arg2, {
                          to: arg1
                        });
                        props.parentId = callId;
                        eachProp(defaultProps, function (value, key) {
                          if (is.und(props[key])) {
                            props[key] = value;
                          }
                        });
                        _context.next = 11;
                        return target.start(props);

                      case 11:
                        result = _context.sent;
                        bailIfEnded(bailSignal);

                        if (!state.paused) {
                          _context.next = 16;
                          break;
                        }

                        _context.next = 16;
                        return new Promise(function (resume) {
                          state.resumeQueue.add(resume);
                        });

                      case 16:
                        return _context.abrupt("return", result);

                      case 17:
                      case "end":
                        return _context.stop();
                    }
                  }
                }, _callee);
              }))();
            };

            if (!globals.skipAnimation) {
              _context3.next = 9;
              break;
            }

            stopAsync(state);
            return _context3.abrupt("return", getFinishedResult(target, false));

          case 9:
            _context3.prev = 9;

            if (is.arr(to)) {
              animating = function () {
                var _ref5 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(queue) {
                  var _iterator2, _step2, _props;

                  return _regeneratorRuntime.wrap(function _callee2$(_context2) {
                    while (1) {
                      switch (_context2.prev = _context2.next) {
                        case 0:
                          _iterator2 = _createForOfIteratorHelperLoose(queue);

                        case 1:
                          if ((_step2 = _iterator2()).done) {
                            _context2.next = 7;
                            break;
                          }

                          _props = _step2.value;
                          _context2.next = 5;
                          return animate(_props);

                        case 5:
                          _context2.next = 1;
                          break;

                        case 7:
                        case "end":
                          return _context2.stop();
                      }
                    }
                  }, _callee2);
                }));

                return function (_x) {
                  return _ref5.apply(this, arguments);
                };
              }()(to);
            } else {
              animating = Promise.resolve(to(animate, target.stop.bind(target)));
            }

            _context3.next = 13;
            return Promise.all([animating.then(preventBail), bailPromise]);

          case 13:
            result = getFinishedResult(target.get(), true, false);
            _context3.next = 27;
            break;

          case 16:
            _context3.prev = 16;
            _context3.t0 = _context3["catch"](9);

            if (!(_context3.t0 instanceof BailSignal)) {
              _context3.next = 22;
              break;
            }

            result = _context3.t0.result;
            _context3.next = 27;
            break;

          case 22:
            if (!(_context3.t0 instanceof SkipAniamtionSignal)) {
              _context3.next = 26;
              break;
            }

            result = _context3.t0.result;
            _context3.next = 27;
            break;

          case 26:
            throw _context3.t0;

          case 27:
            _context3.prev = 27;

            if (callId == state.asyncId) {
              state.asyncId = parentId;
              state.asyncTo = parentId ? prevTo : undefined;
              state.promise = parentId ? prevPromise : undefined;
            }

            return _context3.finish(27);

          case 30:
            if (is.fun(onRest)) {
              raf.batchedUpdates(function () {
                onRest(result, target, target.item);
              });
            }

            return _context3.abrupt("return", result);

          case 32:
          case "end":
            return _context3.stop();
        }
      }
    }, _callee3, null, [[9, 16, 27, 30]]);
  }))();
}

function stopAsync(state, cancelId) {
  flush(state.timeouts, function (t) {
    return t.cancel();
  });
  state.pauseQueue.clear();
  state.resumeQueue.clear();
  state.asyncId = state.asyncTo = state.promise = undefined;
  if (cancelId) state.cancelId = cancelId;
}

var BailSignal = /*#__PURE__*/function (_Error) {
  _inheritsLoose(BailSignal, _Error);

  function BailSignal() {
    var _this;

    _this = _Error.call(this, 'An async animation has been interrupted. You see this error because you ' + 'forgot to use `await` or `.catch(...)` on its returned promise.') || this;
    _this.result = void 0;
    return _this;
  }

  return BailSignal;
}( /*#__PURE__*/_wrapNativeSuper(Error));

var SkipAniamtionSignal = /*#__PURE__*/function (_Error2) {
  _inheritsLoose(SkipAniamtionSignal, _Error2);

  function SkipAniamtionSignal() {
    var _this2;

    _this2 = _Error2.call(this, 'SkipAnimationSignal') || this;
    _this2.result = void 0;
    return _this2;
  }

  return SkipAniamtionSignal;
}( /*#__PURE__*/_wrapNativeSuper(Error));

var isFrameValue = function isFrameValue(value) {
  return value instanceof FrameValue;
};

var nextId$1 = 1;

var FrameValue = /*#__PURE__*/function (_FluidValue) {
  _inheritsLoose(FrameValue, _FluidValue);

  function FrameValue() {
    var _this3;

    for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
      args[_key2] = arguments[_key2];
    }

    _this3 = _FluidValue.call.apply(_FluidValue, [this].concat(args)) || this;
    _this3.id = nextId$1++;
    _this3.key = void 0;
    _this3._priority = 0;
    return _this3;
  }

  var _proto = FrameValue.prototype;

  _proto.get = function get() {
    var node = getAnimated(this);
    return node && node.getValue();
  };

  _proto.to = function to() {
    for (var _len3 = arguments.length, args = new Array(_len3), _key3 = 0; _key3 < _len3; _key3++) {
      args[_key3] = arguments[_key3];
    }

    return globals.to(this, args);
  };

  _proto.interpolate = function interpolate() {
    deprecateInterpolate();

    for (var _len4 = arguments.length, args = new Array(_len4), _key4 = 0; _key4 < _len4; _key4++) {
      args[_key4] = arguments[_key4];
    }

    return globals.to(this, args);
  };

  _proto.toJSON = function toJSON() {
    return this.get();
  };

  _proto.observerAdded = function observerAdded(count) {
    if (count == 1) this._attach();
  };

  _proto.observerRemoved = function observerRemoved(count) {
    if (count == 0) this._detach();
  };

  _proto._attach = function _attach() {};

  _proto._detach = function _detach() {};

  _proto._onChange = function _onChange(value, idle) {
    if (idle === void 0) {
      idle = false;
    }

    callFluidObservers(this, {
      type: 'change',
      parent: this,
      value: value,
      idle: idle
    });
  };

  _proto._onPriorityChange = function _onPriorityChange(priority) {
    if (!this.idle) {
      frameLoop.sort(this);
    }

    callFluidObservers(this, {
      type: 'priority',
      parent: this,
      priority: priority
    });
  };

  _createClass(FrameValue, [{
    key: "priority",
    get: function get() {
      return this._priority;
    },
    set: function set(priority) {
      if (this._priority != priority) {
        this._priority = priority;

        this._onPriorityChange(priority);
      }
    }
  }]);

  return FrameValue;
}(FluidValue);

var $P = /*#__PURE__*/Symbol.for('SpringPhase');
var HAS_ANIMATED = 1;
var IS_ANIMATING = 2;
var IS_PAUSED = 4;

var hasAnimated = function hasAnimated(target) {
  return (target[$P] & HAS_ANIMATED) > 0;
};

var isAnimating = function isAnimating(target) {
  return (target[$P] & IS_ANIMATING) > 0;
};

var isPaused = function isPaused(target) {
  return (target[$P] & IS_PAUSED) > 0;
};

var setActiveBit = function setActiveBit(target, active) {
  return active ? target[$P] |= IS_ANIMATING | HAS_ANIMATED : target[$P] &= ~IS_ANIMATING;
};

var setPausedBit = function setPausedBit(target, paused) {
  return paused ? target[$P] |= IS_PAUSED : target[$P] &= ~IS_PAUSED;
};

var SpringValue = /*#__PURE__*/function (_FrameValue) {
  _inheritsLoose(SpringValue, _FrameValue);

  function SpringValue(arg1, arg2) {
    var _this4;

    _this4 = _FrameValue.call(this) || this;
    _this4.key = void 0;
    _this4.animation = new Animation();
    _this4.queue = void 0;
    _this4.defaultProps = {};
    _this4._state = {
      paused: false,
      pauseQueue: new Set(),
      resumeQueue: new Set(),
      timeouts: new Set()
    };
    _this4._pendingCalls = new Set();
    _this4._lastCallId = 0;
    _this4._lastToId = 0;
    _this4._memoizedDuration = 0;

    if (!is.und(arg1) || !is.und(arg2)) {
      var props = is.obj(arg1) ? _extends({}, arg1) : _extends({}, arg2, {
        from: arg1
      });

      if (is.und(props.default)) {
        props.default = true;
      }

      _this4.start(props);
    }

    return _this4;
  }

  var _proto2 = SpringValue.prototype;

  _proto2.advance = function advance(dt) {
    var _this5 = this;

    var idle = true;
    var changed = false;
    var anim = this.animation;
    var config = anim.config,
        toValues = anim.toValues;
    var payload = getPayload(anim.to);

    if (!payload && hasFluidValue(anim.to)) {
      toValues = toArray(getFluidValue(anim.to));
    }

    anim.values.forEach(function (node, i) {
      if (node.done) return;
      var to = node.constructor == AnimatedString ? 1 : payload ? payload[i].lastPosition : toValues[i];
      var finished = anim.immediate;
      var position = to;

      if (!finished) {
        position = node.lastPosition;

        if (config.tension <= 0) {
          node.done = true;
          return;
        }

        var elapsed = node.elapsedTime += dt;
        var from = anim.fromValues[i];
        var v0 = node.v0 != null ? node.v0 : node.v0 = is.arr(config.velocity) ? config.velocity[i] : config.velocity;
        var velocity;

        if (!is.und(config.duration)) {
          var p = 1;

          if (config.duration > 0) {
            if (_this5._memoizedDuration !== config.duration) {
              _this5._memoizedDuration = config.duration;

              if (node.durationProgress > 0) {
                node.elapsedTime = config.duration * node.durationProgress;
                elapsed = node.elapsedTime += dt;
              }
            }

            p = (config.progress || 0) + elapsed / _this5._memoizedDuration;
            p = p > 1 ? 1 : p < 0 ? 0 : p;
            node.durationProgress = p;
          }

          position = from + config.easing(p) * (to - from);
          velocity = (position - node.lastPosition) / dt;
          finished = p == 1;
        } else if (config.decay) {
          var decay = config.decay === true ? 0.998 : config.decay;
          var e = Math.exp(-(1 - decay) * elapsed);
          position = from + v0 / (1 - decay) * (1 - e);
          finished = Math.abs(node.lastPosition - position) < 0.1;
          velocity = v0 * e;
        } else {
          velocity = node.lastVelocity == null ? v0 : node.lastVelocity;
          var precision = config.precision || (from == to ? 0.005 : Math.min(1, Math.abs(to - from) * 0.001));
          var restVelocity = config.restVelocity || precision / 10;
          var bounceFactor = config.clamp ? 0 : config.bounce;
          var canBounce = !is.und(bounceFactor);
          var isGrowing = from == to ? node.v0 > 0 : from < to;
          var isMoving;
          var isBouncing = false;
          var step = 1;
          var numSteps = Math.ceil(dt / step);

          for (var n = 0; n < numSteps; ++n) {
            isMoving = Math.abs(velocity) > restVelocity;

            if (!isMoving) {
              finished = Math.abs(to - position) <= precision;

              if (finished) {
                break;
              }
            }

            if (canBounce) {
              isBouncing = position == to || position > to == isGrowing;

              if (isBouncing) {
                velocity = -velocity * bounceFactor;
                position = to;
              }
            }

            var springForce = -config.tension * 0.000001 * (position - to);
            var dampingForce = -config.friction * 0.001 * velocity;
            var acceleration = (springForce + dampingForce) / config.mass;
            velocity = velocity + acceleration * step;
            position = position + velocity * step;
          }
        }

        node.lastVelocity = velocity;

        if (Number.isNaN(position)) {
          console.warn("Got NaN while animating:", _this5);
          finished = true;
        }
      }

      if (payload && !payload[i].done) {
        finished = false;
      }

      if (finished) {
        node.done = true;
      } else {
        idle = false;
      }

      if (node.setValue(position, config.round)) {
        changed = true;
      }
    });
    var node = getAnimated(this);
    var currVal = node.getValue();

    if (idle) {
      var finalVal = getFluidValue(anim.to);

      if ((currVal !== finalVal || changed) && !config.decay) {
        node.setValue(finalVal);

        this._onChange(finalVal);
      } else if (changed && config.decay) {
        this._onChange(currVal);
      }

      this._stop();
    } else if (changed) {
      this._onChange(currVal);
    }
  };

  _proto2.set = function set(value) {
    var _this6 = this;

    raf.batchedUpdates(function () {
      _this6._stop();

      _this6._focus(value);

      _this6._set(value);
    });
    return this;
  };

  _proto2.pause = function pause() {
    this._update({
      pause: true
    });
  };

  _proto2.resume = function resume() {
    this._update({
      pause: false
    });
  };

  _proto2.finish = function finish() {
    var _this7 = this;

    if (isAnimating(this)) {
      var _this$animation = this.animation,
          _to = _this$animation.to,
          _config = _this$animation.config;
      raf.batchedUpdates(function () {
        _this7._onStart();

        if (!_config.decay) {
          _this7._set(_to, false);
        }

        _this7._stop();
      });
    }

    return this;
  };

  _proto2.update = function update(props) {
    var queue = this.queue || (this.queue = []);
    queue.push(props);
    return this;
  };

  _proto2.start = function start(to, arg2) {
    var _this8 = this;

    var queue;

    if (!is.und(to)) {
      queue = [is.obj(to) ? to : _extends({}, arg2, {
        to: to
      })];
    } else {
      queue = this.queue || [];
      this.queue = [];
    }

    return Promise.all(queue.map(function (props) {
      return _this8._update(props);
    })).then(function (results) {
      return getCombinedResult(_this8, results);
    });
  };

  _proto2.stop = function stop(cancel) {
    var _this9 = this;

    var to = this.animation.to;

    this._focus(this.get());

    stopAsync(this._state, cancel && this._lastCallId);
    raf.batchedUpdates(function () {
      return _this9._stop(to, cancel);
    });
    return this;
  };

  _proto2.reset = function reset() {
    this._update({
      reset: true
    });
  };

  _proto2.eventObserved = function eventObserved(event) {
    if (event.type == 'change') {
      this._start();
    } else if (event.type == 'priority') {
      this.priority = event.priority + 1;
    }
  };

  _proto2._prepareNode = function _prepareNode(props) {
    var key = this.key || '';
    var to = props.to,
        from = props.from;
    to = is.obj(to) ? to[key] : to;

    if (to == null || isAsyncTo(to)) {
      to = undefined;
    }

    from = is.obj(from) ? from[key] : from;

    if (from == null) {
      from = undefined;
    }

    var range = {
      to: to,
      from: from
    };

    if (!hasAnimated(this)) {
      if (props.reverse) {
        var _ref6 = [from, to];
        to = _ref6[0];
        from = _ref6[1];
      }

      from = getFluidValue(from);

      if (!is.und(from)) {
        this._set(from);
      } else if (!getAnimated(this)) {
        this._set(to);
      }
    }

    return range;
  };

  _proto2._update = function _update(_ref, isLoop) {
    var _this10 = this;

    var props = _extends({}, _ref);

    var key = this.key,
        defaultProps = this.defaultProps;
    if (props.default) Object.assign(defaultProps, getDefaultProps(props, function (value, prop) {
      return /^on/.test(prop) ? resolveProp(value, key) : value;
    }));
    mergeActiveFn(this, props, 'onProps');
    sendEvent(this, 'onProps', props, this);

    var range = this._prepareNode(props);

    if (Object.isFrozen(this)) {
      throw Error('Cannot animate a `SpringValue` object that is frozen. ' + 'Did you forget to pass your component to `animated(...)` before animating its props?');
    }

    var state = this._state;
    return scheduleProps(++this._lastCallId, {
      key: key,
      props: props,
      defaultProps: defaultProps,
      state: state,
      actions: {
        pause: function pause() {
          if (!isPaused(_this10)) {
            setPausedBit(_this10, true);
            flushCalls(state.pauseQueue);
            sendEvent(_this10, 'onPause', getFinishedResult(_this10, checkFinished(_this10, _this10.animation.to)), _this10);
          }
        },
        resume: function resume() {
          if (isPaused(_this10)) {
            setPausedBit(_this10, false);

            if (isAnimating(_this10)) {
              _this10._resume();
            }

            flushCalls(state.resumeQueue);
            sendEvent(_this10, 'onResume', getFinishedResult(_this10, checkFinished(_this10, _this10.animation.to)), _this10);
          }
        },
        start: this._merge.bind(this, range)
      }
    }).then(function (result) {
      if (props.loop && result.finished && !(isLoop && result.noop)) {
        var nextProps = createLoopUpdate(props);

        if (nextProps) {
          return _this10._update(nextProps, true);
        }
      }

      return result;
    });
  };

  _proto2._merge = function _merge(range, props, resolve) {
    var _this11 = this;

    if (props.cancel) {
      this.stop(true);
      return resolve(getCancelledResult(this));
    }

    var hasToProp = !is.und(range.to);
    var hasFromProp = !is.und(range.from);

    if (hasToProp || hasFromProp) {
      if (props.callId > this._lastToId) {
        this._lastToId = props.callId;
      } else {
        return resolve(getCancelledResult(this));
      }
    }

    var key = this.key,
        defaultProps = this.defaultProps,
        anim = this.animation;
    var prevTo = anim.to,
        prevFrom = anim.from;
    var _range$to = range.to,
        to = _range$to === void 0 ? prevTo : _range$to,
        _range$from = range.from,
        from = _range$from === void 0 ? prevFrom : _range$from;

    if (hasFromProp && !hasToProp && (!props.default || is.und(to))) {
      to = from;
    }

    if (props.reverse) {
      var _ref7 = [from, to];
      to = _ref7[0];
      from = _ref7[1];
    }

    var hasFromChanged = !isEqual(from, prevFrom);

    if (hasFromChanged) {
      anim.from = from;
    }

    from = getFluidValue(from);
    var hasToChanged = !isEqual(to, prevTo);

    if (hasToChanged) {
      this._focus(to);
    }

    var hasAsyncTo = isAsyncTo(props.to);
    var config = anim.config;
    var decay = config.decay,
        velocity = config.velocity;

    if (hasToProp || hasFromProp) {
      config.velocity = 0;
    }

    if (props.config && !hasAsyncTo) {
      mergeConfig(config, callProp(props.config, key), props.config !== defaultProps.config ? callProp(defaultProps.config, key) : void 0);
    }

    var node = getAnimated(this);

    if (!node || is.und(to)) {
      return resolve(getFinishedResult(this, true));
    }

    var reset = is.und(props.reset) ? hasFromProp && !props.default : !is.und(from) && matchProp(props.reset, key);
    var value = reset ? from : this.get();
    var goal = computeGoal(to);
    var isAnimatable = is.num(goal) || is.arr(goal) || isAnimatedString(goal);
    var immediate = !hasAsyncTo && (!isAnimatable || matchProp(defaultProps.immediate || props.immediate, key));

    if (hasToChanged) {
      var nodeType = getAnimatedType(to);

      if (nodeType !== node.constructor) {
        if (immediate) {
          node = this._set(goal);
        } else throw Error("Cannot animate between " + node.constructor.name + " and " + nodeType.name + ", as the \"to\" prop suggests");
      }
    }

    var goalType = node.constructor;
    var started = hasFluidValue(to);
    var finished = false;

    if (!started) {
      var hasValueChanged = reset || !hasAnimated(this) && hasFromChanged;

      if (hasToChanged || hasValueChanged) {
        finished = isEqual(computeGoal(value), goal);
        started = !finished;
      }

      if (!isEqual(anim.immediate, immediate) && !immediate || !isEqual(config.decay, decay) || !isEqual(config.velocity, velocity)) {
        started = true;
      }
    }

    if (finished && isAnimating(this)) {
      if (anim.changed && !reset) {
        started = true;
      } else if (!started) {
        this._stop(prevTo);
      }
    }

    if (!hasAsyncTo) {
      if (started || hasFluidValue(prevTo)) {
        anim.values = node.getPayload();
        anim.toValues = hasFluidValue(to) ? null : goalType == AnimatedString ? [1] : toArray(goal);
      }

      if (anim.immediate != immediate) {
        anim.immediate = immediate;

        if (!immediate && !reset) {
          this._set(prevTo);
        }
      }

      if (started) {
        var onRest = anim.onRest;
        each(ACTIVE_EVENTS, function (type) {
          return mergeActiveFn(_this11, props, type);
        });
        var result = getFinishedResult(this, checkFinished(this, prevTo));
        flushCalls(this._pendingCalls, result);

        this._pendingCalls.add(resolve);

        if (anim.changed) raf.batchedUpdates(function () {
          anim.changed = !reset;
          onRest == null ? void 0 : onRest(result, _this11);

          if (reset) {
            callProp(defaultProps.onRest, result);
          } else {
            anim.onStart == null ? void 0 : anim.onStart(result, _this11);
          }
        });
      }
    }

    if (reset) {
      this._set(value);
    }

    if (hasAsyncTo) {
      resolve(runAsync(props.to, props, this._state, this));
    } else if (started) {
      this._start();
    } else if (isAnimating(this) && !hasToChanged) {
      this._pendingCalls.add(resolve);
    } else {
      resolve(getNoopResult(value));
    }
  };

  _proto2._focus = function _focus(value) {
    var anim = this.animation;

    if (value !== anim.to) {
      if (getFluidObservers(this)) {
        this._detach();
      }

      anim.to = value;

      if (getFluidObservers(this)) {
        this._attach();
      }
    }
  };

  _proto2._attach = function _attach() {
    var priority = 0;
    var to = this.animation.to;

    if (hasFluidValue(to)) {
      addFluidObserver(to, this);

      if (isFrameValue(to)) {
        priority = to.priority + 1;
      }
    }

    this.priority = priority;
  };

  _proto2._detach = function _detach() {
    var to = this.animation.to;

    if (hasFluidValue(to)) {
      removeFluidObserver(to, this);
    }
  };

  _proto2._set = function _set(arg, idle) {
    var _this12 = this;

    if (idle === void 0) {
      idle = true;
    }

    var value = getFluidValue(arg);

    if (!is.und(value)) {
      var oldNode = getAnimated(this);

      if (!oldNode || !isEqual(value, oldNode.getValue())) {
        var nodeType = getAnimatedType(value);

        if (!oldNode || oldNode.constructor != nodeType) {
          setAnimated(this, nodeType.create(value));
        } else {
          oldNode.setValue(value);
        }

        if (oldNode) {
          raf.batchedUpdates(function () {
            _this12._onChange(value, idle);
          });
        }
      }
    }

    return getAnimated(this);
  };

  _proto2._onStart = function _onStart() {
    var anim = this.animation;

    if (!anim.changed) {
      anim.changed = true;
      sendEvent(this, 'onStart', getFinishedResult(this, checkFinished(this, anim.to)), this);
    }
  };

  _proto2._onChange = function _onChange(value, idle) {
    if (!idle) {
      this._onStart();

      callProp(this.animation.onChange, value, this);
    }

    callProp(this.defaultProps.onChange, value, this);

    _FrameValue.prototype._onChange.call(this, value, idle);
  };

  _proto2._start = function _start() {
    var anim = this.animation;
    getAnimated(this).reset(getFluidValue(anim.to));

    if (!anim.immediate) {
      anim.fromValues = anim.values.map(function (node) {
        return node.lastPosition;
      });
    }

    if (!isAnimating(this)) {
      setActiveBit(this, true);

      if (!isPaused(this)) {
        this._resume();
      }
    }
  };

  _proto2._resume = function _resume() {
    if (globals.skipAnimation) {
      this.finish();
    } else {
      frameLoop.start(this);
    }
  };

  _proto2._stop = function _stop(goal, cancel) {
    if (isAnimating(this)) {
      setActiveBit(this, false);
      var anim = this.animation;
      each(anim.values, function (node) {
        node.done = true;
      });

      if (anim.toValues) {
        anim.onChange = anim.onPause = anim.onResume = undefined;
      }

      callFluidObservers(this, {
        type: 'idle',
        parent: this
      });
      var result = cancel ? getCancelledResult(this.get()) : getFinishedResult(this.get(), checkFinished(this, goal != null ? goal : anim.to));
      flushCalls(this._pendingCalls, result);

      if (anim.changed) {
        anim.changed = false;
        sendEvent(this, 'onRest', result, this);
      }
    }
  };

  _createClass(SpringValue, [{
    key: "idle",
    get: function get() {
      return !(isAnimating(this) || this._state.asyncTo) || isPaused(this);
    }
  }, {
    key: "goal",
    get: function get() {
      return getFluidValue(this.animation.to);
    }
  }, {
    key: "velocity",
    get: function get() {
      var node = getAnimated(this);
      return node instanceof AnimatedValue ? node.lastVelocity || 0 : node.getPayload().map(function (node) {
        return node.lastVelocity || 0;
      });
    }
  }, {
    key: "hasAnimated",
    get: function get() {
      return hasAnimated(this);
    }
  }, {
    key: "isAnimating",
    get: function get() {
      return isAnimating(this);
    }
  }, {
    key: "isPaused",
    get: function get() {
      return isPaused(this);
    }
  }]);

  return SpringValue;
}(FrameValue);

function checkFinished(target, to) {
  var goal = computeGoal(to);
  var value = computeGoal(target.get());
  return isEqual(value, goal);
}

function createLoopUpdate(props, loop, to) {
  if (loop === void 0) {
    loop = props.loop;
  }

  if (to === void 0) {
    to = props.to;
  }

  var loopRet = callProp(loop);

  if (loopRet) {
    var overrides = loopRet !== true && inferTo(loopRet);
    var reverse = (overrides || props).reverse;
    var reset = !overrides || overrides.reset;
    return createUpdate(_extends({}, props, {
      loop: loop,
      default: false,
      pause: undefined,
      to: !reverse || isAsyncTo(to) ? to : undefined,
      from: reset ? props.from : undefined,
      reset: reset
    }, overrides));
  }
}

function createUpdate(props) {
  var _props2 = props = inferTo(props),
      to = _props2.to,
      from = _props2.from;

  var keys = new Set();
  if (is.obj(to)) findDefined(to, keys);
  if (is.obj(from)) findDefined(from, keys);
  props.keys = keys.size ? Array.from(keys) : null;
  return props;
}

function findDefined(values, keys) {
  eachProp(values, function (value, key) {
    return value != null && keys.add(key);
  });
}

var ACTIVE_EVENTS = ['onStart', 'onRest', 'onChange', 'onPause', 'onResume'];

function mergeActiveFn(target, props, type) {
  target.animation[type] = props[type] !== getDefaultProp(props, type) ? resolveProp(props[type], target.key) : undefined;
}

function sendEvent(target, type) {
  var _target$animation$typ2, _target$defaultProps$2;

  var _target$animation$typ, _target$animation, _target$defaultProps$, _target$defaultProps;

  for (var _len5 = arguments.length, args = new Array(_len5 > 2 ? _len5 - 2 : 0), _key5 = 2; _key5 < _len5; _key5++) {
    args[_key5 - 2] = arguments[_key5];
  }

  (_target$animation$typ = (_target$animation = target.animation)[type]) == null ? void 0 : (_target$animation$typ2 = _target$animation$typ).call.apply(_target$animation$typ2, [_target$animation].concat(args));
  (_target$defaultProps$ = (_target$defaultProps = target.defaultProps)[type]) == null ? void 0 : (_target$defaultProps$2 = _target$defaultProps$).call.apply(_target$defaultProps$2, [_target$defaultProps].concat(args));
}

var BATCHED_EVENTS = ['onStart', 'onChange', 'onRest'];
var nextId = 1;

var Controller = /*#__PURE__*/function () {
  function Controller(props, flush) {
    this.id = nextId++;
    this.springs = {};
    this.queue = [];
    this.ref = void 0;
    this._flush = void 0;
    this._initialProps = void 0;
    this._lastAsyncId = 0;
    this._active = new Set();
    this._changed = new Set();
    this._started = false;
    this._item = void 0;
    this._state = {
      paused: false,
      pauseQueue: new Set(),
      resumeQueue: new Set(),
      timeouts: new Set()
    };
    this._events = {
      onStart: new Map(),
      onChange: new Map(),
      onRest: new Map()
    };
    this._onFrame = this._onFrame.bind(this);

    if (flush) {
      this._flush = flush;
    }

    if (props) {
      this.start(_extends({
        default: true
      }, props));
    }
  }

  var _proto3 = Controller.prototype;

  _proto3.get = function get() {
    var values = {};
    this.each(function (spring, key) {
      return values[key] = spring.get();
    });
    return values;
  };

  _proto3.set = function set(values) {
    for (var key in values) {
      var value = values[key];

      if (!is.und(value)) {
        this.springs[key].set(value);
      }
    }
  };

  _proto3.update = function update(props) {
    if (props) {
      this.queue.push(createUpdate(props));
    }

    return this;
  };

  _proto3.start = function start(props) {
    var queue = this.queue;

    if (props) {
      queue = toArray(props).map(createUpdate);
    } else {
      this.queue = [];
    }

    if (this._flush) {
      return this._flush(this, queue);
    }

    prepareKeys(this, queue);
    return flushUpdateQueue(this, queue);
  };

  _proto3.stop = function stop(arg, keys) {
    if (arg !== !!arg) {
      keys = arg;
    }

    if (keys) {
      var springs = this.springs;
      each(toArray(keys), function (key) {
        return springs[key].stop(!!arg);
      });
    } else {
      stopAsync(this._state, this._lastAsyncId);
      this.each(function (spring) {
        return spring.stop(!!arg);
      });
    }

    return this;
  };

  _proto3.pause = function pause(keys) {
    if (is.und(keys)) {
      this.start({
        pause: true
      });
    } else {
      var springs = this.springs;
      each(toArray(keys), function (key) {
        return springs[key].pause();
      });
    }

    return this;
  };

  _proto3.resume = function resume(keys) {
    if (is.und(keys)) {
      this.start({
        pause: false
      });
    } else {
      var springs = this.springs;
      each(toArray(keys), function (key) {
        return springs[key].resume();
      });
    }

    return this;
  };

  _proto3.each = function each(iterator) {
    eachProp(this.springs, iterator);
  };

  _proto3._onFrame = function _onFrame() {
    var _this13 = this;

    var _this$_events = this._events,
        onStart = _this$_events.onStart,
        onChange = _this$_events.onChange,
        onRest = _this$_events.onRest;
    var active = this._active.size > 0;
    var changed = this._changed.size > 0;

    if (active && !this._started || changed && !this._started) {
      this._started = true;
      flush(onStart, function (_ref8) {
        var onStart = _ref8[0],
            result = _ref8[1];
        result.value = _this13.get();
        onStart(result, _this13, _this13._item);
      });
    }

    var idle = !active && this._started;
    var values = changed || idle && onRest.size ? this.get() : null;

    if (changed && onChange.size) {
      flush(onChange, function (_ref9) {
        var onChange = _ref9[0],
            result = _ref9[1];
        result.value = values;
        onChange(result, _this13, _this13._item);
      });
    }

    if (idle) {
      this._started = false;
      flush(onRest, function (_ref10) {
        var onRest = _ref10[0],
            result = _ref10[1];
        result.value = values;
        onRest(result, _this13, _this13._item);
      });
    }
  };

  _proto3.eventObserved = function eventObserved(event) {
    if (event.type == 'change') {
      this._changed.add(event.parent);

      if (!event.idle) {
        this._active.add(event.parent);
      }
    } else if (event.type == 'idle') {
      this._active.delete(event.parent);
    } else return;

    raf.onFrame(this._onFrame);
  };

  _createClass(Controller, [{
    key: "idle",
    get: function get() {
      return !this._state.asyncTo && Object.values(this.springs).every(function (spring) {
        return spring.idle;
      });
    }
  }, {
    key: "item",
    get: function get() {
      return this._item;
    },
    set: function set(item) {
      this._item = item;
    }
  }]);

  return Controller;
}();

function flushUpdateQueue(ctrl, queue) {
  return Promise.all(queue.map(function (props) {
    return flushUpdate(ctrl, props);
  })).then(function (results) {
    return getCombinedResult(ctrl, results);
  });
}

function flushUpdate(_x2, _x3, _x4) {
  return _flushUpdate.apply(this, arguments);
}

function _flushUpdate() {
  _flushUpdate = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4(ctrl, props, isLoop) {
    var keys, to, from, loop, onRest, onResolve, defaults, asyncTo, state, promises, cancel, result, nextProps;
    return _regeneratorRuntime.wrap(function _callee4$(_context4) {
      while (1) {
        switch (_context4.prev = _context4.next) {
          case 0:
            keys = props.keys, to = props.to, from = props.from, loop = props.loop, onRest = props.onRest, onResolve = props.onResolve;
            defaults = is.obj(props.default) && props.default;

            if (loop) {
              props.loop = false;
            }

            if (to === false) props.to = null;
            if (from === false) props.from = null;
            asyncTo = is.arr(to) || is.fun(to) ? to : undefined;

            if (asyncTo) {
              props.to = undefined;
              props.onRest = undefined;

              if (defaults) {
                defaults.onRest = undefined;
              }
            } else {
              each(BATCHED_EVENTS, function (key) {
                var handler = props[key];

                if (is.fun(handler)) {
                  var queue = ctrl['_events'][key];

                  props[key] = function (_ref17) {
                    var finished = _ref17.finished,
                        cancelled = _ref17.cancelled;
                    var result = queue.get(handler);

                    if (result) {
                      if (!finished) result.finished = false;
                      if (cancelled) result.cancelled = true;
                    } else {
                      queue.set(handler, {
                        value: null,
                        finished: finished || false,
                        cancelled: cancelled || false
                      });
                    }
                  };

                  if (defaults) {
                    defaults[key] = props[key];
                  }
                }
              });
            }

            state = ctrl['_state'];

            if (props.pause === !state.paused) {
              state.paused = props.pause;
              flushCalls(props.pause ? state.pauseQueue : state.resumeQueue);
            } else if (state.paused) {
              props.pause = true;
            }

            promises = (keys || Object.keys(ctrl.springs)).map(function (key) {
              return ctrl.springs[key].start(props);
            });
            cancel = props.cancel === true || getDefaultProp(props, 'cancel') === true;

            if (asyncTo || cancel && state.asyncId) {
              promises.push(scheduleProps(++ctrl['_lastAsyncId'], {
                props: props,
                state: state,
                actions: {
                  pause: noop,
                  resume: noop,
                  start: function start(props, resolve) {
                    if (cancel) {
                      stopAsync(state, ctrl['_lastAsyncId']);
                      resolve(getCancelledResult(ctrl));
                    } else {
                      props.onRest = onRest;
                      resolve(runAsync(asyncTo, props, state, ctrl));
                    }
                  }
                }
              }));
            }

            if (!state.paused) {
              _context4.next = 15;
              break;
            }

            _context4.next = 15;
            return new Promise(function (resume) {
              state.resumeQueue.add(resume);
            });

          case 15:
            _context4.t0 = getCombinedResult;
            _context4.t1 = ctrl;
            _context4.next = 19;
            return Promise.all(promises);

          case 19:
            _context4.t2 = _context4.sent;
            result = (0, _context4.t0)(_context4.t1, _context4.t2);

            if (!(loop && result.finished && !(isLoop && result.noop))) {
              _context4.next = 26;
              break;
            }

            nextProps = createLoopUpdate(props, loop, to);

            if (!nextProps) {
              _context4.next = 26;
              break;
            }

            prepareKeys(ctrl, [nextProps]);
            return _context4.abrupt("return", flushUpdate(ctrl, nextProps, true));

          case 26:
            if (onResolve) {
              raf.batchedUpdates(function () {
                return onResolve(result, ctrl, ctrl.item);
              });
            }

            return _context4.abrupt("return", result);

          case 28:
          case "end":
            return _context4.stop();
        }
      }
    }, _callee4);
  }));
  return _flushUpdate.apply(this, arguments);
}

function getSprings(ctrl, props) {
  var springs = _extends({}, ctrl.springs);

  if (props) {
    each(toArray(props), function (props) {
      if (is.und(props.keys)) {
        props = createUpdate(props);
      }

      if (!is.obj(props.to)) {
        props = _extends({}, props, {
          to: undefined
        });
      }

      prepareSprings(springs, props, function (key) {
        return createSpring(key);
      });
    });
  }

  setSprings(ctrl, springs);
  return springs;
}

function setSprings(ctrl, springs) {
  eachProp(springs, function (spring, key) {
    if (!ctrl.springs[key]) {
      ctrl.springs[key] = spring;
      addFluidObserver(spring, ctrl);
    }
  });
}

function createSpring(key, observer) {
  var spring = new SpringValue();
  spring.key = key;

  if (observer) {
    addFluidObserver(spring, observer);
  }

  return spring;
}

function prepareSprings(springs, props, create) {
  if (props.keys) {
    each(props.keys, function (key) {
      var spring = springs[key] || (springs[key] = create(key));
      spring['_prepareNode'](props);
    });
  }
}

function prepareKeys(ctrl, queue) {
  each(queue, function (props) {
    prepareSprings(ctrl.springs, props, function (key) {
      return createSpring(key, ctrl);
    });
  });
}

function _objectWithoutPropertiesLoose$1(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

var _excluded$3 = ["children"];

var SpringContext = function SpringContext(_ref) {
  var children = _ref.children,
      props = _objectWithoutPropertiesLoose$1(_ref, _excluded$3);

  var inherited = useContext(ctx);
  var pause = props.pause || !!inherited.pause,
      immediate = props.immediate || !!inherited.immediate;
  props = useMemoOne(function () {
    return {
      pause: pause,
      immediate: immediate
    };
  }, [pause, immediate]);
  var Provider = ctx.Provider;
  return /*#__PURE__*/React.createElement(Provider, {
    value: props
  }, children);
};

var ctx = /*#__PURE__*/makeContext(SpringContext, {});
SpringContext.Provider = ctx.Provider;
SpringContext.Consumer = ctx.Consumer;

function makeContext(target, init) {
  Object.assign(target, /*#__PURE__*/React.createContext(init));
  target.Provider._context = target;
  target.Consumer._context = target;
  return target;
}

var SpringRef = function SpringRef() {
  var current = [];

  var SpringRef = function SpringRef(props) {
    deprecateDirectCall();
    var results = [];
    each(current, function (ctrl, i) {
      if (is.und(props)) {
        results.push(ctrl.start());
      } else {
        var _update2 = _getProps(props, ctrl, i);

        if (_update2) {
          results.push(ctrl.start(_update2));
        }
      }
    });
    return results;
  };

  SpringRef.current = current;

  SpringRef.add = function (ctrl) {
    if (!current.includes(ctrl)) {
      current.push(ctrl);
    }
  };

  SpringRef.delete = function (ctrl) {
    var i = current.indexOf(ctrl);
    if (~i) current.splice(i, 1);
  };

  SpringRef.pause = function () {
    var _arguments = arguments;
    each(current, function (ctrl) {
      return ctrl.pause.apply(ctrl, _arguments);
    });
    return this;
  };

  SpringRef.resume = function () {
    var _arguments2 = arguments;
    each(current, function (ctrl) {
      return ctrl.resume.apply(ctrl, _arguments2);
    });
    return this;
  };

  SpringRef.set = function (values) {
    each(current, function (ctrl) {
      return ctrl.set(values);
    });
  };

  SpringRef.start = function (props) {
    var _this14 = this;

    var results = [];
    each(current, function (ctrl, i) {
      if (is.und(props)) {
        results.push(ctrl.start());
      } else {
        var _update3 = _this14._getProps(props, ctrl, i);

        if (_update3) {
          results.push(ctrl.start(_update3));
        }
      }
    });
    return results;
  };

  SpringRef.stop = function () {
    var _arguments3 = arguments;
    each(current, function (ctrl) {
      return ctrl.stop.apply(ctrl, _arguments3);
    });
    return this;
  };

  SpringRef.update = function (props) {
    var _this15 = this;

    each(current, function (ctrl, i) {
      return ctrl.update(_this15._getProps(props, ctrl, i));
    });
    return this;
  };

  var _getProps = function _getProps(arg, ctrl, index) {
    return is.fun(arg) ? arg(index, ctrl) : arg;
  };

  SpringRef._getProps = _getProps;
  return SpringRef;
};

var TransitionPhase;

(function (TransitionPhase) {
  TransitionPhase["MOUNT"] = "mount";
  TransitionPhase["ENTER"] = "enter";
  TransitionPhase["UPDATE"] = "update";
  TransitionPhase["LEAVE"] = "leave";
})(TransitionPhase || (TransitionPhase = {}));

function useTransition(data, props, deps) {
  var _arguments5 = arguments;
  var propsFn = is.fun(props) && props;

  var _ref11 = propsFn ? propsFn() : props,
      reset = _ref11.reset,
      sort = _ref11.sort,
      _ref11$trail = _ref11.trail,
      trail = _ref11$trail === void 0 ? 0 : _ref11$trail,
      _ref11$expires = _ref11.expires,
      expires = _ref11$expires === void 0 ? true : _ref11$expires,
      onDestroyed = _ref11.onDestroyed,
      propsRef = _ref11.ref,
      propsConfig = _ref11.config;

  var ref = useMemo(function () {
    return propsFn || _arguments5.length == 3 ? SpringRef() : void 0;
  }, []);
  var items = toArray(data);
  var transitions = [];
  var usedTransitions = useRef(null);
  var prevTransitions = reset ? null : usedTransitions.current;
  useLayoutEffect(function () {
    usedTransitions.current = transitions;
  });
  useOnce(function () {
    return function () {
      return each(usedTransitions.current, function (t) {
        if (t.expired) {
          clearTimeout(t.expirationId);
        }

        detachRefs(t.ctrl, ref);
        t.ctrl.stop(true);
      });
    };
  });
  var keys = getKeys(items, propsFn ? propsFn() : props, prevTransitions);
  var expired = reset && usedTransitions.current || [];
  useLayoutEffect(function () {
    return each(expired, function (_ref12) {
      var ctrl = _ref12.ctrl,
          item = _ref12.item,
          key = _ref12.key;
      detachRefs(ctrl, ref);
      callProp(onDestroyed, item, key);
    });
  });
  var reused = [];
  if (prevTransitions) each(prevTransitions, function (t, i) {
    if (t.expired) {
      clearTimeout(t.expirationId);
      expired.push(t);
    } else {
      i = reused[i] = keys.indexOf(t.key);
      if (~i) transitions[i] = t;
    }
  });
  each(items, function (item, i) {
    if (!transitions[i]) {
      transitions[i] = {
        key: keys[i],
        item: item,
        phase: TransitionPhase.MOUNT,
        ctrl: new Controller()
      };
      transitions[i].ctrl.item = item;
    }
  });

  if (reused.length) {
    var i = -1;

    var _ref13 = propsFn ? propsFn() : props,
        leave = _ref13.leave;

    each(reused, function (keyIndex, prevIndex) {
      var t = prevTransitions[prevIndex];

      if (~keyIndex) {
        i = transitions.indexOf(t);
        transitions[i] = _extends({}, t, {
          item: items[keyIndex]
        });
      } else if (leave) {
        transitions.splice(++i, 0, t);
      }
    });
  }

  if (is.fun(sort)) {
    transitions.sort(function (a, b) {
      return sort(a.item, b.item);
    });
  }

  var delay = -trail;
  var forceUpdate = useForceUpdate();
  var defaultProps = getDefaultProps(props);
  var changes = new Map();
  each(transitions, function (t, i) {
    var key = t.key;
    var prevPhase = t.phase;
    var p = propsFn ? propsFn() : props;
    var to;
    var phase;
    var propsDelay = callProp(p.delay || 0, key);

    if (prevPhase == TransitionPhase.MOUNT) {
      to = p.enter;
      phase = TransitionPhase.ENTER;
    } else {
      var isLeave = keys.indexOf(key) < 0;

      if (prevPhase != TransitionPhase.LEAVE) {
        if (isLeave) {
          to = p.leave;
          phase = TransitionPhase.LEAVE;
        } else if (to = p.update) {
          phase = TransitionPhase.UPDATE;
        } else return;
      } else if (!isLeave) {
        to = p.enter;
        phase = TransitionPhase.ENTER;
      } else return;
    }

    to = callProp(to, t.item, i);
    to = is.obj(to) ? inferTo(to) : {
      to: to
    };

    if (!to.config) {
      var _config2 = propsConfig || defaultProps.config;

      to.config = callProp(_config2, t.item, i, phase);
    }

    delay += trail;

    var payload = _extends({}, defaultProps, {
      delay: propsDelay + delay,
      ref: propsRef,
      immediate: p.immediate,
      reset: false
    }, to);

    if (phase == TransitionPhase.ENTER && is.und(payload.from)) {
      var _p = propsFn ? propsFn() : props;

      var from = is.und(_p.initial) || prevTransitions ? _p.from : _p.initial;
      payload.from = callProp(from, t.item, i);
    }

    var onResolve = payload.onResolve;

    payload.onResolve = function (result) {
      callProp(onResolve, result);
      var transitions = usedTransitions.current;
      var t = transitions.find(function (t) {
        return t.key === key;
      });
      if (!t) return;

      if (result.cancelled && t.phase != TransitionPhase.UPDATE) {
        return;
      }

      if (t.ctrl.idle) {
        var idle = transitions.every(function (t) {
          return t.ctrl.idle;
        });

        if (t.phase == TransitionPhase.LEAVE) {
          var expiry = callProp(expires, t.item);

          if (expiry !== false) {
            var expiryMs = expiry === true ? 0 : expiry;
            t.expired = true;

            if (!idle && expiryMs > 0) {
              if (expiryMs <= 0x7fffffff) t.expirationId = setTimeout(forceUpdate, expiryMs);
              return;
            }
          }
        }

        if (idle && transitions.some(function (t) {
          return t.expired;
        })) {
          forceUpdate();
        }
      }
    };

    var springs = getSprings(t.ctrl, payload);
    changes.set(t, {
      phase: phase,
      springs: springs,
      payload: payload
    });
  });
  var context = useContext(SpringContext);
  var prevContext = usePrev(context);
  var hasContext = context !== prevContext && hasProps(context);
  useLayoutEffect(function () {
    if (hasContext) each(transitions, function (t) {
      t.ctrl.start({
        default: context
      });
    });
  }, [context]);
  useLayoutEffect(function () {
    each(changes, function (_ref14, t) {
      var phase = _ref14.phase,
          payload = _ref14.payload;
      var ctrl = t.ctrl;
      t.phase = phase;
      ref == null ? void 0 : ref.add(ctrl);

      if (hasContext && phase == TransitionPhase.ENTER) {
        ctrl.start({
          default: context
        });
      }

      if (payload) {
        replaceRef(ctrl, payload.ref);

        if (ctrl.ref) {
          ctrl.update(payload);
        } else {
          ctrl.start(payload);
        }
      }
    });
  }, reset ? void 0 : deps);

  var renderTransitions = function renderTransitions(render) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, transitions.map(function (t, i) {
      var _ref15 = changes.get(t) || t.ctrl,
          springs = _ref15.springs;

      var elem = render(_extends({}, springs), t.item, t, i);
      return elem && elem.type ? /*#__PURE__*/React.createElement(elem.type, _extends({}, elem.props, {
        key: is.str(t.key) || is.num(t.key) ? t.key : t.ctrl.id,
        ref: elem.ref
      })) : elem;
    }));
  };

  return ref ? [renderTransitions, ref] : renderTransitions;
}

var nextKey = 1;

function getKeys(items, _ref16, prevTransitions) {
  var key = _ref16.key,
      _ref16$keys = _ref16.keys,
      keys = _ref16$keys === void 0 ? key : _ref16$keys;

  if (keys === null) {
    var reused = new Set();
    return items.map(function (item) {
      var t = prevTransitions && prevTransitions.find(function (t) {
        return t.item === item && t.phase !== TransitionPhase.LEAVE && !reused.has(t);
      });

      if (t) {
        reused.add(t);
        return t.key;
      }

      return nextKey++;
    });
  }

  return is.und(keys) ? items : is.fun(keys) ? items.map(keys) : toArray(keys);
}

var Interpolation = /*#__PURE__*/function (_FrameValue2) {
  _inheritsLoose(Interpolation, _FrameValue2);

  function Interpolation(source, args) {
    var _this16;

    _this16 = _FrameValue2.call(this) || this;
    _this16.key = void 0;
    _this16.idle = true;
    _this16.calc = void 0;
    _this16._active = new Set();
    _this16.source = source;
    _this16.calc = createInterpolator.apply(void 0, args);

    var value = _this16._get();

    var nodeType = getAnimatedType(value);
    setAnimated(_assertThisInitialized(_this16), nodeType.create(value));
    return _this16;
  }

  var _proto4 = Interpolation.prototype;

  _proto4.advance = function advance(_dt) {
    var value = this._get();

    var oldValue = this.get();

    if (!isEqual(value, oldValue)) {
      getAnimated(this).setValue(value);

      this._onChange(value, this.idle);
    }

    if (!this.idle && checkIdle(this._active)) {
      becomeIdle(this);
    }
  };

  _proto4._get = function _get() {
    var inputs = is.arr(this.source) ? this.source.map(getFluidValue) : toArray(getFluidValue(this.source));
    return this.calc.apply(this, inputs);
  };

  _proto4._start = function _start() {
    var _this17 = this;

    if (this.idle && !checkIdle(this._active)) {
      this.idle = false;
      each(getPayload(this), function (node) {
        node.done = false;
      });

      if (globals.skipAnimation) {
        raf.batchedUpdates(function () {
          return _this17.advance();
        });
        becomeIdle(this);
      } else {
        frameLoop.start(this);
      }
    }
  };

  _proto4._attach = function _attach() {
    var _this18 = this;

    var priority = 1;
    each(toArray(this.source), function (source) {
      if (hasFluidValue(source)) {
        addFluidObserver(source, _this18);
      }

      if (isFrameValue(source)) {
        if (!source.idle) {
          _this18._active.add(source);
        }

        priority = Math.max(priority, source.priority + 1);
      }
    });
    this.priority = priority;

    this._start();
  };

  _proto4._detach = function _detach() {
    var _this19 = this;

    each(toArray(this.source), function (source) {
      if (hasFluidValue(source)) {
        removeFluidObserver(source, _this19);
      }
    });

    this._active.clear();

    becomeIdle(this);
  };

  _proto4.eventObserved = function eventObserved(event) {
    if (event.type == 'change') {
      if (event.idle) {
        this.advance();
      } else {
        this._active.add(event.parent);

        this._start();
      }
    } else if (event.type == 'idle') {
      this._active.delete(event.parent);
    } else if (event.type == 'priority') {
      this.priority = toArray(this.source).reduce(function (highest, parent) {
        return Math.max(highest, (isFrameValue(parent) ? parent.priority : 0) + 1);
      }, 0);
    }
  };

  return Interpolation;
}(FrameValue);

function isIdle(source) {
  return source.idle !== false;
}

function checkIdle(active) {
  return !active.size || Array.from(active).every(isIdle);
}

function becomeIdle(self) {
  if (!self.idle) {
    self.idle = true;
    each(getPayload(self), function (node) {
      node.done = true;
    });
    callFluidObservers(self, {
      type: 'idle',
      parent: self
    });
  }
}

globals.assign({
  createStringInterpolator: createStringInterpolator,
  to: function to(source, args) {
    return new Interpolation(source, args);
  }
});

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

var _excluded$2 = ["style", "children", "scrollTop", "scrollLeft"];
var isCustomPropRE = /^--/;

function dangerousStyleValue(name, value) {
  if (value == null || typeof value === 'boolean' || value === '') return '';
  if (typeof value === 'number' && value !== 0 && !isCustomPropRE.test(name) && !(isUnitlessNumber.hasOwnProperty(name) && isUnitlessNumber[name])) return value + 'px';
  return ('' + value).trim();
}

var attributeCache = {};

function applyAnimatedValues(instance, props) {
  if (!instance.nodeType || !instance.setAttribute) {
    return false;
  }

  var isFilterElement = instance.nodeName === 'filter' || instance.parentNode && instance.parentNode.nodeName === 'filter';

  var _ref = props,
      style = _ref.style,
      children = _ref.children,
      scrollTop = _ref.scrollTop,
      scrollLeft = _ref.scrollLeft,
      attributes = _objectWithoutPropertiesLoose(_ref, _excluded$2);

  var values = Object.values(attributes);
  var names = Object.keys(attributes).map(function (name) {
    return isFilterElement || instance.hasAttribute(name) ? name : attributeCache[name] || (attributeCache[name] = name.replace(/([A-Z])/g, function (n) {
      return '-' + n.toLowerCase();
    }));
  });

  if (children !== void 0) {
    instance.textContent = children;
  }

  for (var name in style) {
    if (style.hasOwnProperty(name)) {
      var value = dangerousStyleValue(name, style[name]);

      if (isCustomPropRE.test(name)) {
        instance.style.setProperty(name, value);
      } else {
        instance.style[name] = value;
      }
    }
  }

  names.forEach(function (name, i) {
    instance.setAttribute(name, values[i]);
  });

  if (scrollTop !== void 0) {
    instance.scrollTop = scrollTop;
  }

  if (scrollLeft !== void 0) {
    instance.scrollLeft = scrollLeft;
  }
}

var isUnitlessNumber = {
  animationIterationCount: true,
  borderImageOutset: true,
  borderImageSlice: true,
  borderImageWidth: true,
  boxFlex: true,
  boxFlexGroup: true,
  boxOrdinalGroup: true,
  columnCount: true,
  columns: true,
  flex: true,
  flexGrow: true,
  flexPositive: true,
  flexShrink: true,
  flexNegative: true,
  flexOrder: true,
  gridRow: true,
  gridRowEnd: true,
  gridRowSpan: true,
  gridRowStart: true,
  gridColumn: true,
  gridColumnEnd: true,
  gridColumnSpan: true,
  gridColumnStart: true,
  fontWeight: true,
  lineClamp: true,
  lineHeight: true,
  opacity: true,
  order: true,
  orphans: true,
  tabSize: true,
  widows: true,
  zIndex: true,
  zoom: true,
  fillOpacity: true,
  floodOpacity: true,
  stopOpacity: true,
  strokeDasharray: true,
  strokeDashoffset: true,
  strokeMiterlimit: true,
  strokeOpacity: true,
  strokeWidth: true
};

var prefixKey = function prefixKey(prefix, key) {
  return prefix + key.charAt(0).toUpperCase() + key.substring(1);
};

var prefixes = ['Webkit', 'Ms', 'Moz', 'O'];
isUnitlessNumber = /*#__PURE__*/Object.keys(isUnitlessNumber).reduce(function (acc, prop) {
  prefixes.forEach(function (prefix) {
    return acc[prefixKey(prefix, prop)] = acc[prop];
  });
  return acc;
}, isUnitlessNumber);
var _excluded$1 = ["x", "y", "z"];
var domTransforms = /^(matrix|translate|scale|rotate|skew)/;
var pxTransforms = /^(translate)/;
var degTransforms = /^(rotate|skew)/;

var addUnit = function addUnit(value, unit) {
  return is.num(value) && value !== 0 ? value + unit : value;
};

var isValueIdentity = function isValueIdentity(value, id) {
  return is.arr(value) ? value.every(function (v) {
    return isValueIdentity(v, id);
  }) : is.num(value) ? value === id : parseFloat(value) === id;
};

var AnimatedStyle = /*#__PURE__*/function (_AnimatedObject) {
  _inheritsLoose(AnimatedStyle, _AnimatedObject);

  function AnimatedStyle(_ref) {
    var x = _ref.x,
        y = _ref.y,
        z = _ref.z,
        style = _objectWithoutPropertiesLoose(_ref, _excluded$1);

    var inputs = [];
    var transforms = [];

    if (x || y || z) {
      inputs.push([x || 0, y || 0, z || 0]);
      transforms.push(function (xyz) {
        return ["translate3d(" + xyz.map(function (v) {
          return addUnit(v, 'px');
        }).join(',') + ")", isValueIdentity(xyz, 0)];
      });
    }

    eachProp(style, function (value, key) {
      if (key === 'transform') {
        inputs.push([value || '']);
        transforms.push(function (transform) {
          return [transform, transform === ''];
        });
      } else if (domTransforms.test(key)) {
        delete style[key];
        if (is.und(value)) return;
        var unit = pxTransforms.test(key) ? 'px' : degTransforms.test(key) ? 'deg' : '';
        inputs.push(toArray(value));
        transforms.push(key === 'rotate3d' ? function (_ref2) {
          var x = _ref2[0],
              y = _ref2[1],
              z = _ref2[2],
              deg = _ref2[3];
          return ["rotate3d(" + x + "," + y + "," + z + "," + addUnit(deg, unit) + ")", isValueIdentity(deg, 0)];
        } : function (input) {
          return [key + "(" + input.map(function (v) {
            return addUnit(v, unit);
          }).join(',') + ")", isValueIdentity(input, key.startsWith('scale') ? 1 : 0)];
        });
      }
    });

    if (inputs.length) {
      style.transform = new FluidTransform(inputs, transforms);
    }

    return _AnimatedObject.call(this, style) || this;
  }

  return AnimatedStyle;
}(AnimatedObject);

var FluidTransform = /*#__PURE__*/function (_FluidValue) {
  _inheritsLoose(FluidTransform, _FluidValue);

  function FluidTransform(inputs, transforms) {
    var _this;

    _this = _FluidValue.call(this) || this;
    _this._value = null;
    _this.inputs = inputs;
    _this.transforms = transforms;
    return _this;
  }

  var _proto = FluidTransform.prototype;

  _proto.get = function get() {
    return this._value || (this._value = this._get());
  };

  _proto._get = function _get() {
    var _this2 = this;

    var transform = '';
    var identity = true;
    each(this.inputs, function (input, i) {
      var arg1 = getFluidValue(input[0]);

      var _this2$transforms$i = _this2.transforms[i](is.arr(arg1) ? arg1 : input.map(getFluidValue)),
          t = _this2$transforms$i[0],
          id = _this2$transforms$i[1];

      transform += ' ' + t;
      identity = identity && id;
    });
    return identity ? 'none' : transform;
  };

  _proto.observerAdded = function observerAdded(count) {
    var _this3 = this;

    if (count == 1) each(this.inputs, function (input) {
      return each(input, function (value) {
        return hasFluidValue(value) && addFluidObserver(value, _this3);
      });
    });
  };

  _proto.observerRemoved = function observerRemoved(count) {
    var _this4 = this;

    if (count == 0) each(this.inputs, function (input) {
      return each(input, function (value) {
        return hasFluidValue(value) && removeFluidObserver(value, _this4);
      });
    });
  };

  _proto.eventObserved = function eventObserved(event) {
    if (event.type == 'change') {
      this._value = null;
    }

    callFluidObservers(this, event);
  };

  return FluidTransform;
}(FluidValue);

var primitives = ['a', 'abbr', 'address', 'area', 'article', 'aside', 'audio', 'b', 'base', 'bdi', 'bdo', 'big', 'blockquote', 'body', 'br', 'button', 'canvas', 'caption', 'cite', 'code', 'col', 'colgroup', 'data', 'datalist', 'dd', 'del', 'details', 'dfn', 'dialog', 'div', 'dl', 'dt', 'em', 'embed', 'fieldset', 'figcaption', 'figure', 'footer', 'form', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'head', 'header', 'hgroup', 'hr', 'html', 'i', 'iframe', 'img', 'input', 'ins', 'kbd', 'keygen', 'label', 'legend', 'li', 'link', 'main', 'map', 'mark', 'menu', 'menuitem', 'meta', 'meter', 'nav', 'noscript', 'object', 'ol', 'optgroup', 'option', 'output', 'p', 'param', 'picture', 'pre', 'progress', 'q', 'rp', 'rt', 'ruby', 's', 'samp', 'script', 'section', 'select', 'small', 'source', 'span', 'strong', 'style', 'sub', 'summary', 'sup', 'table', 'tbody', 'td', 'textarea', 'tfoot', 'th', 'thead', 'time', 'title', 'tr', 'track', 'u', 'ul', 'var', 'video', 'wbr', 'circle', 'clipPath', 'defs', 'ellipse', 'foreignObject', 'g', 'image', 'line', 'linearGradient', 'mask', 'path', 'pattern', 'polygon', 'polyline', 'radialGradient', 'rect', 'stop', 'svg', 'text', 'tspan'];
var _excluded = ["scrollTop", "scrollLeft"];
globals.assign({
  batchedUpdates: unstable_batchedUpdates,
  createStringInterpolator: createStringInterpolator,
  colors: colors
});
var host = /*#__PURE__*/createHost(primitives, {
  applyAnimatedValues: applyAnimatedValues,
  createAnimatedStyle: function createAnimatedStyle(style) {
    return new AnimatedStyle(style);
  },
  getComponentProps: function getComponentProps(_ref) {
    var props = _objectWithoutPropertiesLoose(_ref, _excluded);

    return props;
  }
});
var animated = host.animated;

var DEFAULT_ELEMENT$h = 'div';
var Overlay = /*#__PURE__*/React__default.forwardRef(function AOOverlay(_a, ref) {
  var children = _a.children,
      className = _a.className,
      _b = _a.as,
      as = _b === void 0 ? DEFAULT_ELEMENT$h : _b,
      isOpen = _a.isOpen,
      props = __rest(_a, ["children", "className", "as", "isOpen"]);

  var transitions = useTransition(isOpen, __assign({
    from: {
      opacity: 0
    },
    enter: {
      opacity: 1
    },
    leave: {
      opacity: 0
    }
  }, config.stiff));
  var ElementType = animated[as];
  return /*#__PURE__*/React__default.createElement(Fragment, null, transitions(function (style, item) {
    return item && /*#__PURE__*/React__default.createElement(ElementType, __assign({
      className: classnames('bg-overlay fixed inset-0', isOpen ? 'pointer-events-auto' : 'pointer-events-none', className),
      "data-aods": "overlay"
    }, props, {
      style: __assign({}, style),
      ref: ref
    }), children);
  }));
});

function useBodyScrollLock(targetRef, isLocked) {
  useEffect(function () {
    var options = {
      reserveScrollBarGap: true
    };

    if (targetRef === null || targetRef === void 0 ? void 0 : targetRef.current) {
      if (isLocked) {
        disableBodyScroll(targetRef.current, options);
      } else {
        enableBodyScroll(targetRef.current);
      }
    }

    return function () {
      return clearAllBodyScrollLocks();
    };
  }, [isLocked, targetRef]);
}

function useClickAway(ref, onClickAway) {
  useEffect(function () {
    if (!document || !ref.current || !onClickAway) return;

    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        onClickAway === null || onClickAway === void 0 ? void 0 : onClickAway(event);
      }
    }

    document.addEventListener('mousedown', handleClickOutside); // eslint-disable-next-line consistent-return

    return function () {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref, onClickAway]);
}

function useConfig() {
  return useContext(DesignSystemContext);
}

function useForwardedRef(r) {
  var innerRef = useRef(null);
  useEffect(function () {
    if (!r) return;

    if (typeof r === 'function') {
      r(innerRef.current);
    } else {
      r.current = innerRef.current;
    }
  }, [r]);
  return innerRef;
}

function useKeyDown(_a) {
  var isEnabled = _a.isEnabled,
      key = _a.key,
      onKeyDown = _a.onKeyDown;
  var handleKeyDown = useCallback(function (e) {
    if (key && (Array.isArray(key) ? key.includes(e.key) : e.key !== key)) {
      return;
    }

    onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(e);
  }, [key, onKeyDown]);
  useEffect(function () {
    if (isEnabled && onKeyDown) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return function () {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isEnabled, handleKeyDown, onKeyDown]);
}

function useTranslation(language, translations) {
  var t = useMemo(function () {
    return translations[language];
  }, [language, translations]);
  return useCallback(function (key) {
    var args = [];

    for (var _i = 1; _i < arguments.length; _i++) {
      args[_i - 1] = arguments[_i];
    }

    var value = t[key];
    return value.apply(void 0, args);
  }, [t]);
}

var translations$3 = {
  en: {
    close: function close() {
      return 'Close';
    }
  },
  de: {
    close: function close() {
      return 'Schließen';
    }
  }
};

var DEFAULT_ELEMENT$g = 'div';
var animationProps = {
  bottom: {
    from: {
      transform: 'translateY(100%)',
      maxHeight: '85%'
    },
    enter: {
      transform: 'translateY(0%)'
    },
    leave: {
      transform: 'translateY(100%)'
    }
  },
  left: {
    from: {
      transform: 'translateX(-100%)'
    },
    enter: {
      transform: 'translateX(0%)'
    },
    leave: {
      transform: 'translateX(-100%)'
    }
  },
  right: {
    from: {
      transform: 'translateX(100%)'
    },
    enter: {
      transform: 'translateX(0%)'
    },
    leave: {
      transform: 'translateX(100%)'
    }
  },
  top: {
    from: {
      transform: 'translateY(-100%)',
      maxHeight: '85%'
    },
    enter: {
      transform: 'translateY(0%)'
    },
    leave: {
      transform: 'translateY(-100%)'
    }
  }
};
var FocusLockSidecar$1 = /*#__PURE__*/sidecar(function () {
  return import(
  /* webpackPrefetch: true */
  'react-focus-lock/sidecar');
});
var Drawer = /*#__PURE__*/React__default.forwardRef(function AODrawer(_a, ref) {
  var _b = _a.as,
      as = _b === void 0 ? DEFAULT_ELEMENT$g : _b,
      children = _a.children,
      className = _a.className,
      _c = _a.isOpen,
      isOpen = _c === void 0 ? false : _c,
      onClose = _a.onClose,
      _d = _a.edge,
      edge = _d === void 0 ? 'bottom' : _d,
      _e = _a.hideCloseButton,
      hideCloseButton = _e === void 0 ? false : _e,
      _f = _a.closeIcon,
      closeIcon = _f === void 0 ? 'close' : _f,
      title = _a.title,
      titleReverse = _a.titleReverse,
      _g = _a.persist,
      persist = _g === void 0 ? false : _g,
      portalTarget = _a.portalTarget,
      props = __rest(_a, ["as", "children", "className", "isOpen", "onClose", "edge", "hideCloseButton", "closeIcon", "title", "titleReverse", "persist", "portalTarget"]);

  var _h = useConfig(),
      language = _h.language,
      isSSR = _h.isSSR;

  var t = useTranslation(language, translations$3);
  var transitions = useTransition(isOpen, __assign(__assign(__assign({}, animationProps[edge]), config.stiff), {
    expires: !persist
  })); // TODO: Get element type from `as`

  var innerRef = useForwardedRef(ref);
  useBodyScrollLock(innerRef, isOpen);
  useClickAway(innerRef, isOpen ? onClose : undefined);
  var ElementType = animated[as];
  useKeyDown({
    isEnabled: isOpen,
    key: 'Escape',
    onKeyDown: onClose
  });
  var container = portalTarget || document && document.body;
  return !isSSR && container ? /*#__PURE__*/createPortal( /*#__PURE__*/React__default.createElement(Fragment, null, transitions(function (style, item) {
    return item && /*#__PURE__*/React__default.createElement("div", {
      className: classnames('fixed inset-0 z-50', !isOpen && 'pointer-events-none')
    }, /*#__PURE__*/React__default.createElement(Overlay, {
      isOpen: isOpen,
      "data-testid": "drawer-overlay"
    }), /*#__PURE__*/React__default.createElement(ElementType // Coerce type to override legacy ref conflict
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    , __assign({
      // Coerce type to override legacy ref conflict
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ref: innerRef,
      style: __assign({}, style),
      className: classnames('absolute z-50 p-4 bg-white shadow-2dp overflow-auto', {
        'w-full bottom-0 rounded-t-lg': edge === 'bottom',
        'h-full w-11/12 md:w-3/4 lg:w-1/2 xl:w-5/12 left-0': edge === 'left',
        'h-full w-11/12 md:w-3/4 lg:w-1/2 xl:w-5/12 right-0': edge === 'right',
        'w-full top-0 rounded-b-lg': edge === 'top'
      }, className),
      role: "dialog",
      "data-aods": "drawer"
    }, props), /*#__PURE__*/React__default.createElement(FocusLock, {
      disabled: !isOpen,
      returnFocus: true,
      sideCar: FocusLockSidecar$1
    }, /*#__PURE__*/React__default.createElement(AutoFocusInside, null, /*#__PURE__*/React__default.createElement("div", {
      className: classnames('flex items-center', titleReverse ? 'flex-row-reverse justify-end' : 'justify-between')
    }, /*#__PURE__*/React__default.isValidElement(title) ? /*#__PURE__*/React__default.cloneElement(title, {
      className: classnames('m-0', title.props.className)
    }) : title, !hideCloseButton && /*#__PURE__*/React__default.createElement(Button, {
      className: classnames(titleReverse ? 'mr-4' : 'ml-auto'),
      icon: true,
      color: "tertiary",
      onClick: onClose,
      "aria-label": t('close')
    }, /*#__PURE__*/React__default.createElement(AoIcon, {
      icon: closeIcon,
      size: "xl"
    }))), children))));
  })), container) : null;
});

var translations$2 = {
  en: {
    close: function close() {
      return 'Close';
    }
  },
  de: {
    close: function close() {
      return 'Schließen';
    }
  }
};

var DEFAULT_ELEMENT$f = 'div';
var FocusLockSidecar = /*#__PURE__*/sidecar(function () {
  return import(
  /* webpackPrefetch: true */
  'react-focus-lock/sidecar');
});
var Modal = /*#__PURE__*/React__default.forwardRef(function AOModal(_a, ref) {
  var children = _a.children,
      _b = _a.as,
      as = _b === void 0 ? DEFAULT_ELEMENT$f : _b,
      className = _a.className,
      hideCloseButton = _a.hideCloseButton,
      _c = _a.isOpen,
      isOpen = _c === void 0 ? false : _c,
      disableCloseHelpers = _a.disableCloseHelpers,
      onClose = _a.onClose,
      _d = _a.size,
      size = _d === void 0 ? 'md' : _d,
      portalTarget = _a.portalTarget,
      title = _a.title,
      inheritTextStyle = _a.inheritTextStyle,
      _e = _a.persist,
      persist = _e === void 0 ? false : _e,
      props = __rest(_a, ["children", "as", "className", "hideCloseButton", "isOpen", "disableCloseHelpers", "onClose", "size", "portalTarget", "title", "inheritTextStyle", "persist"]);

  var _f = useConfig(),
      language = _f.language,
      isSSR = _f.isSSR;

  var t = useTranslation(language, translations$2);
  var transitions = useTransition(isOpen, __assign(__assign({
    from: {
      transform: 'scale(0.75)',
      opacity: 0
    },
    enter: {
      transform: 'scale(1)',
      opacity: 1
    },
    leave: {
      transform: 'scale(0.75)',
      opacity: 0
    }
  }, config.stiff), {
    expires: !persist
  }));
  var ElementType = animated[as];
  var modalContentRef = useForwardedRef(ref);
  var previousFocusRef = useRef(null);
  useClickAway(modalContentRef, !disableCloseHelpers && isOpen ? onClose : undefined);
  useEffect(function () {
    if (isOpen) {
      previousFocusRef.current = document.activeElement;
    } else if (previousFocusRef.current && previousFocusRef.current.focus) {
      previousFocusRef.current.focus();
    }
  }, [isOpen]);
  useBodyScrollLock(modalContentRef, isOpen);
  useKeyDown({
    isEnabled: !disableCloseHelpers && isOpen,
    key: 'Escape',
    onKeyDown: onClose
  });
  var container = portalTarget || document && document.body;
  return !isSSR && container ? /*#__PURE__*/createPortal( /*#__PURE__*/React__default.createElement(Fragment, null, transitions(function (style, item) {
    return item &&
    /*#__PURE__*/
    // Use fixed z-index rather than class to display above header
    React__default.createElement("div", {
      className: classnames('fixed inset-0', !isOpen && 'pointer-events-none'),
      style: {
        zIndex: 200
      }
    }, /*#__PURE__*/React__default.createElement(Overlay, {
      isOpen: isOpen,
      "data-testid": "modal-overlay"
    }), /*#__PURE__*/React__default.createElement("div", {
      className: "fixed inset-0 flex flex-col items-center p-4 overflow-y-auto"
    }, /*#__PURE__*/React__default.createElement(ElementType, __assign({
      className: classnames('bg-white rounded-lg relative p-4 lg:p-6 text-body shadow-2dp my-auto max-w-full overflow-x-auto', !inheritTextStyle && 'text-body', size !== 'auto' && "max-w-screen-".concat(size, " w-full"), className),
      // Coerce type to override legacy ref conflict
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ref: modalContentRef,
      role: "dialog",
      "aria-modal": true,
      style: __assign({}, style),
      "data-aods": "modal"
    }, props), /*#__PURE__*/React__default.createElement(FocusLock, {
      sideCar: FocusLockSidecar,
      disabled: !isOpen
    }, /*#__PURE__*/React__default.createElement(AutoFocusInside, null, (title || !hideCloseButton) && /*#__PURE__*/React__default.createElement("div", {
      className: "flex items-center justify-between pb-1"
    }, /*#__PURE__*/React__default.isValidElement(title) && /*#__PURE__*/React__default.cloneElement(title, {
      className: classnames('m-0', title.props.className)
    }), !hideCloseButton && /*#__PURE__*/React__default.createElement(Button, {
      className: "ml-auto",
      variant: "tertiary",
      icon: true,
      onClick: onClose,
      "aria-label": t('close')
    }, /*#__PURE__*/React__default.createElement(AoIcon, {
      icon: "close",
      size: "xl"
    }))), children)))));
  })), container) : null;
});

var DEFAULT_ELEMENT$e = 'span';
var Tag = /*#__PURE__*/React__default.forwardRef(function AOTag(_a, ref) {
  var _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$e : _b,
      children = _a.children,
      className = _a.className,
      _c = _a.color,
      color = _c === void 0 ? 'core' : _c,
      square = _a.square,
      border = _a.border,
      inheritTextStyle = _a.inheritTextStyle,
      _d = _a.variant,
      variant = _d === void 0 ? 'pill' : _d,
      inverse = _a.inverse,
      noPadding = _a.noPadding,
      noFlex = _a.noFlex,
      props = __rest(_a, ["as", "children", "className", "color", "square", "border", "inheritTextStyle", "variant", "inverse", "noPadding", "noFlex"]);

  var isCustomColor = color === 'custom';
  var isBare = variant === 'bare';
  var isPill = variant === 'pill';
  var isUnderline = variant === 'underline';
  var bgClassNameSuffix = inverse ? '-contrast' : '';
  var textClassNameSuffix = inverse ? '' : "-contrast";
  return /*#__PURE__*/React__default.createElement(ElementType, __assign({
    className: classnames(className, 'inline-block border border-solid', !inheritTextStyle && ['text-body-sm', !isBare && 'font-bold'], !isCustomColor && [border ? "border-ui-".concat(color, "-accent") : 'border-transparent', !inheritTextStyle && "text-ui-".concat(color).concat(textClassNameSuffix), isPill && "bg-ui-".concat(color).concat(bgClassNameSuffix)], (isBare || isCustomColor && !border) && 'border-transparent', !isBare && !square && 'rounded-full', isUnderline && 'inline-flex flex-col', !noPadding && 'py-2 px-3'),
    ref: ref,
    "data-aods": "tag"
  }, props), /*#__PURE__*/React__default.createElement("span", {
    className: classnames(!noFlex && 'flex items-center')
  }, children), isUnderline && /*#__PURE__*/React__default.createElement("span", {
    className: classnames('h-1 rounded-full w-full inline-block mt-1', "bg-ui-".concat(color).concat(textClassNameSuffix))
  }));
});

var translations$1 = {
  en: {
    energyRating: function energyRating(r) {
      return "".concat(r ? "".concat(r, " ") : '', "Energy Rating");
    }
  },
  de: {
    energyRating: function energyRating(r) {
      return "".concat(r ? "".concat(r, " ") : '', "Energieeffizienzklasse");
    }
  }
};

var SCALE = ['A+++', 'A++', 'A+', 'A', 'B', 'C', 'D', 'E', 'F', 'G'];
var ENDPOINTS = {
  beta: 'https://design-system-beta.fe.ao-qa.com/assets/labels',
  staging: 'https://staging-assets.ao-qa.com/design-system/assets/labels',
  production: 'https://assets.ao.com/design-system/assets/labels'
};
var mapRatingToBackgroundColor = function mapRatingToBackgroundColor(ratings, highestRating) {
  var lowestRatingIndex = Math.max.apply(Math, ratings.map(function (r) {
    return SCALE.indexOf(r);
  }));
  var highestRatingIndex = SCALE.indexOf(highestRating);
  var defaultColor = '#EE1B24';

  if (lowestRatingIndex === -1 || highestRatingIndex === -1) {
    return defaultColor;
  }

  var rank = lowestRatingIndex - highestRatingIndex;

  switch (rank) {
    case 0:
      return '#00A751';

    case 1:
      return '#4DBC35';

    case 2:
      return '#B6DA18';

    case 3:
      return '#FEF100';

    case 4:
      return '#FBB20B';

    case 5:
      return '#F25D1C';

    default:
      return defaultColor;
  }
};
var DEFAULT_ELEMENT$d = 'a';
var EnergyLabel = /*#__PURE__*/React__default.forwardRef(function AOEnergyLabel(_a, ref) {
  var _b = _a.as,
      as = _b === void 0 ? DEFAULT_ELEMENT$d : _b,
      children = _a.children,
      className = _a.className,
      rating = _a.rating,
      _c = _a.highestRating,
      highestRating = _c === void 0 ? 'A+++' : _c,
      modalProps = _a.modalProps,
      _d = _a.use2020Label,
      use2020Label = _d === void 0 ? false : _d,
      _e = _a.environment,
      environment = _e === void 0 ? 'production' : _e,
      _f = _a.reverse,
      reverse = _f === void 0 ? false : _f,
      props = __rest(_a, ["as", "children", "className", "rating", "highestRating", "modalProps", "use2020Label", "environment", "reverse"]);

  var language = useConfig().language;
  var t = useTranslation(language, translations$1);

  var _g = useState(false),
      isModalOpen = _g[0],
      setModalOpen = _g[1];

  var handleClick = useCallback(function (e) {
    setModalOpen(true);
    e.preventDefault();
  }, []);
  var handleModalClose = useCallback(function () {
    setModalOpen(false);
  }, []);
  var labelSrc = props.href || props.to;
  var linkProps = {};

  if (labelSrc) {
    linkProps = {
      target: '_blank',
      rel: 'noopener noreferrer',
      onClick: handleClick
    };
  }

  var ratings = useMemo(function () {
    return Array.isArray(rating) ? rating : [rating];
  }, [rating]);
  var ratingsString = ratings.join('/');
  var backgroundColor = mapRatingToBackgroundColor(ratings, use2020Label ? 'A' : highestRating);
  var ElementType = as === DEFAULT_ELEMENT$d && !labelSrc ? 'strong' : as;
  return /*#__PURE__*/React__default.createElement(Fragment, null, use2020Label ? /*#__PURE__*/React__default.createElement(ElementType, __assign({}, props, {
    className: className
  }, linkProps, {
    "data-aods": "energy-label"
  }, props), /*#__PURE__*/React__default.createElement("img", {
    src: "".concat(ENDPOINTS[environment], "/energy-label.svg?rating=").concat(ratings.join(','), "&reverse=").concat(reverse.toString()),
    alt: t('energyRating', ratingsString),
    style: {
      height: '2em',
      width: 'auto'
    }
  })) : /*#__PURE__*/React__default.createElement(Tag, __assign({
    "data-aods": "energy-label"
  }, props, {
    as: as,
    color: "custom",
    square: true,
    className: classnames('border-none text-base text-white no-underline relative', className),
    ref: ref,
    style: {
      backgroundColor: backgroundColor
    }
  }, linkProps), ratingsString, /*#__PURE__*/React__default.createElement("span", {
    className: "sr-only"
  }, t('energyRating')), /*#__PURE__*/React__default.createElement("span", {
    className: classnames('absolute top-0 triangle triangle-offset', reverse ? 'triangle-left' : 'triangle-right'),
    style: {
      color: backgroundColor
    },
    "aria-hidden": true
  }), children), labelSrc && /*#__PURE__*/React__default.createElement(Modal, __assign({}, modalProps, {
    isOpen: isModalOpen,
    onClose: handleModalClose,
    size: "auto"
  }), /*#__PURE__*/React__default.createElement("img", {
    src: labelSrc,
    alt: t('energyRating', ratings.join('/')),
    className: "max-w-full"
  })));
});

var DEFAULT_ELEMENT$c = 'div';
var Field = /*#__PURE__*/React__default.forwardRef(function AOField(_a, ref) {
  var _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$c : _b,
      children = _a.children,
      className = _a.className,
      name = _a.name,
      state = _a.state,
      props = __rest(_a, ["as", "children", "className", "name", "state"]);

  var inputId = "".concat(name, "-input");
  var messageId = "".concat(name, "-message");
  var renderProps = {
    labelProps: {
      htmlFor: inputId
    },
    inputProps: {
      name: name,
      'id': inputId,
      'aria-describedby': messageId,
      state: state
    },
    messageProps: {
      id: messageId,
      state: state
    }
  };
  return /*#__PURE__*/React__default.createElement(ElementType, __assign({
    "data-aods": "field"
  }, props, {
    className: classnames('flex flex-col max-w-md space-y-1 mb-4', className),
    ref: ref
  }), children(renderProps));
});

// TODO: Get from Tokens pkg
var BREAKPOINTS = ['def', 'sm', 'md', 'lg', 'xl'];

var getBreakpointVariantClassName = function getBreakpointVariantClassName(bp, value) {
  if (bp === 'def') return "".concat(value);
  return "".concat(bp, ":").concat(value);
};
var parseResponsiveProp = (function (propValue, transform) {
  if (transform === void 0) {
    transform = function transform(value) {
      return "".concat(value);
    };
  }

  if (Array.isArray(propValue)) {
    return propValue.map(function (value, index) {
      if (value === null) return false;
      var bp = BREAKPOINTS[index];
      return getBreakpointVariantClassName(bp, transform(value));
    }).filter(function (value) {
      return !!value;
    });
  } else if (typeof propValue === 'object') {
    return Object.keys(propValue).map(function (bp) {
      var value = propValue[bp];
      return getBreakpointVariantClassName(bp, transform(value));
    });
  }

  return [transform(propValue)];
});

var DEFAULT_ELEMENT$b = 'div';
var Grid = /*#__PURE__*/React__default.forwardRef(function AOGrid(_a, ref) {
  var children = _a.children,
      className = _a.className,
      _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$b : _b,
      noGutter = _a.noGutter,
      reverse = _a.reverse,
      column = _a.column,
      equalHeight = _a.equalHeight,
      vAlign = _a.vAlign,
      hAlign = _a.hAlign,
      noWrap = _a.noWrap,
      props = __rest(_a, ["children", "className", "as", "noGutter", "reverse", "column", "equalHeight", "vAlign", "hAlign", "noWrap"]);

  return /*#__PURE__*/React__default.createElement(ElementType, __assign({
    className: classnames('flex', !noWrap && 'flex-wrap', !noGutter && '-m-2', column && parseResponsiveProp(column, function () {
      return 'flex-col';
    }), reverse && parseResponsiveProp(reverse, function () {
      return "flex-".concat(column ? 'col' : 'row', "-reverse");
    }), column ? [hAlign && "items-".concat(hAlign), vAlign && "justify-".concat(vAlign)] : [vAlign && "items-".concat(vAlign), hAlign && "justify-".concat(hAlign)], className),
    "data-aods": "grid"
  }, props, {
    ref: ref
  }), noGutter ? children : React__default.Children.map(children, function (child) {
    return /*#__PURE__*/React__default.isValidElement(child) ? /*#__PURE__*/React__default.cloneElement(child, {
      className: classnames('p-2', {
        flex: equalHeight
      }, child.props.className)
    }) : child;
  }));
});

var DEFAULT_ELEMENT$a = 'div';

var spanToClassName = function spanToClassName(cols) {
  switch (cols) {
    case 'auto':
      return 'flex-grow';

    case 'inline':
      return 'w-auto';

    case 12:
      return 'w-full';

    default:
      return "w-".concat(cols, "/12");
  }
};

var GridItem = /*#__PURE__*/React__default.forwardRef(function AOGridItem(_a, ref) {
  var children = _a.children,
      className = _a.className,
      _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$a : _b,
      span = _a.span,
      props = __rest(_a, ["children", "className", "as", "span"]);

  return /*#__PURE__*/React__default.createElement(ElementType, __assign({
    className: classnames(span ? parseResponsiveProp(span, spanToClassName) : 'flex-grow', className)
  }, props, {
    "data-aods": "grid-item",
    ref: ref
  }), children);
});

var getInputTheme = (function (state, isDisabled) {
  if (isDisabled === void 0) {
    isDisabled = false;
  }

  var border = classnames({
    'border-gray-40': isDisabled || !state,
    'border-success': !isDisabled && state === 'success',
    'border-attention': !isDisabled && state === 'attention',
    'border-error': !isDisabled && state === 'error',
    'border-highlight': !isDisabled && state === 'highlight'
  });
  var background = classnames({
    'bg-gray-20': isDisabled,
    'bg-success': !isDisabled && state === 'success',
    'bg-attention': !isDisabled && state === 'attention',
    'bg-error': !isDisabled && state === 'error',
    'bg-highlight': !isDisabled && state === 'highlight'
  });
  var color = classnames({
    'text-gray-60': isDisabled,
    'text-cta-main': !isDisabled && state === 'success',
    'text-rating': !isDisabled && state === 'attention',
    'text-error': !isDisabled && state === 'error',
    'text-ice': !isDisabled && state === 'highlight'
  });
  return {
    border: border,
    background: background,
    color: color
  };
});

var DEFAULT_ELEMENT$9 = 'input';
var Input = /*#__PURE__*/React__default.forwardRef(function AOInput(_a, ref) {
  var _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$9 : _b,
      className = _a.className,
      state = _a.state,
      disabled = _a.disabled,
      _c = _a.type,
      type = _c === void 0 ? 'text' : _c,
      _d = _a.seamless,
      seamless = _d === void 0 ? false : _d,
      _e = _a.fullWidth,
      fullWidth = _e === void 0 ? false : _e,
      inheritTextStyle = _a.inheritTextStyle,
      props = __rest(_a, ["as", "className", "state", "disabled", "type", "seamless", "fullWidth", "inheritTextStyle"]);

  var _f = getInputTheme(state, disabled),
      border = _f.border,
      background = _f.background;

  return /*#__PURE__*/React__default.createElement(ElementType, __assign({
    className: classnames('p-3 text-body leading-snug rounded placeholder-gray focus:outline-none appearance-none', !inheritTextStyle && 'text-body', fullWidth && 'w-full', seamless ? 'border-0 bg-transparent' : 'border border-solid focus:shadow-outline-blue', !seamless && [border, background], className),
    disabled: disabled,
    "aria-invalid": state === 'error' || undefined,
    "data-aods": "input"
  }, props, {
    ref: ref,
    type: type
  }));
});

var DEFAULT_ELEMENT$8 = 'div';
var InputContainer = /*#__PURE__*/React__default.forwardRef(function AOInputContainer(_a, ref) {
  var children = _a.children,
      className = _a.className,
      _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$8 : _b,
      _c = _a.width,
      width = _c === void 0 ? 8 : _c,
      action = _a.action,
      state = _a.state,
      disabled = _a.disabled,
      props = __rest(_a, ["children", "className", "as", "width", "action", "state", "disabled"]);

  var _d = getInputTheme(state, disabled),
      border = _d.border,
      background = _d.background,
      color = _d.color;

  var renderProps = {
    inputProps: {
      seamless: true,
      fullWidth: true,
      className: classnames(!action && "pr-".concat(width)),
      state: state,
      disabled: disabled
    },
    iconProps: {
      className: classnames('flex items-center justify-center', "w-".concat(width), color, {
        'pointer-events-none absolute inset-y-0 right-0': !action,
        'border-0 rounded-tl-none rounded-bl-none': action
      })
    }
  };
  return /*#__PURE__*/React__default.createElement(ElementType, __assign({
    "data-aods": "input-container"
  }, props, {
    className: classnames('flex relative rounded border border-solid focus-within:shadow-outline-blue', border, background, className),
    ref: ref
  }), children(renderProps));
});

var DEFAULT_ELEMENT$7 = 'div';
var InputMessage = /*#__PURE__*/React__default.forwardRef(function AOInputMessage(_a, ref) {
  var children = _a.children,
      className = _a.className,
      _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$7 : _b,
      state = _a.state,
      props = __rest(_a, ["children", "className", "as", "state"]);

  var color = {
    'text-tertiary': typeof state === 'undefined',
    'text-success': state === 'success',
    'text-attention': state === 'attention',
    'text-error': state === 'error',
    'text-highlight': state === 'highlight'
  };
  return /*#__PURE__*/React__default.createElement(ElementType, __assign({
    className: classnames('text-body-sm', color, className),
    "aria-live": "assertive",
    "aria-atomic": "true",
    "data-aods": "input-message"
  }, props, {
    ref: ref
  }), children);
});

var DEFAULT_ELEMENT$6 = 'label';
var Label = /*#__PURE__*/React__default.forwardRef(function AOLabel(_a, ref) {
  var _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$6 : _b,
      children = _a.children,
      className = _a.className,
      srOnly = _a.srOnly,
      inheritTextStyle = _a.inheritTextStyle,
      props = __rest(_a, ["as", "children", "className", "srOnly", "inheritTextStyle"]);

  return /*#__PURE__*/React__default.createElement(ElementType, __assign({
    "data-aods": "label"
  }, props, {
    className: srOnly ? 'sr-only' : classnames(!inheritTextStyle && 'text-body', className),
    ref: ref
  }), children);
});

var DEFAULT_ELEMENT$5 = 'div';
var MESSAGE_POSITION_CLASS = {
  left: 'flex-row-reverse space-x-4 space-x-reverse',
  right: 'space-x-4',
  top: 'flex-col-reverse space-y-4 space-y-reverse',
  bottom: 'flex-col space-y-4'
};
var MAIN_COLORS = {
  white: 'text-white',
  dark: 'text-water'
};
var CONTRAST_COLORS = {
  white: 'text-gray-50',
  dark: 'text-steam'
};
var LoadingSpinner = /*#__PURE__*/React__default.forwardRef(function AOLoadingSpinner(_a, ref) {
  var _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$5 : _b,
      className = _a.className,
      _c = _a.small,
      small = _c === void 0 ? false : _c,
      message = _a.message,
      _d = _a.icon,
      icon = _d === void 0 ? 'ao-logo' : _d,
      _e = _a.inheritTextStyle,
      inheritTextStyle = _e === void 0 ? false : _e,
      _f = _a.messagePosition,
      messagePosition = _f === void 0 ? 'bottom' : _f,
      variant = _a.variant,
      _g = _a.inline,
      inline = _g === void 0 ? false : _g,
      _h = _a.hideIcon,
      hideIcon = _h === void 0 ? false : _h,
      props = __rest(_a, ["as", "className", "small", "message", "icon", "inheritTextStyle", "messagePosition", "variant", "inline", "hideIcon"]);

  var contrastColor = variant ? CONTRAST_COLORS[variant] : 'text-lightgreen';
  var mainColor = variant ? MAIN_COLORS[variant] : 'text-ongreen';
  var messageColor = variant ? MAIN_COLORS[variant] : 'text-secondary';
  return /*#__PURE__*/React__default.createElement(ElementType, __assign({
    "data-aods": "loading-spinner",
    id: "loading-spinner"
  }, props, {
    className: classnames(inline ? 'inline-flex' : 'flex items-center justify-center', MESSAGE_POSITION_CLASS[messagePosition], className),
    ref: ref
  }), /*#__PURE__*/React__default.createElement("div", {
    className: classnames('relative flex items-center justify-center', small ? 'h-8 w-8' : 'h-16 w-16')
  }, /*#__PURE__*/React__default.createElement("svg", {
    className: "absolute animate-spin h-full w-full overflow-visible",
    fill: "none",
    viewBox: "0 0 100 100"
  }, /*#__PURE__*/React__default.createElement("circle", {
    className: contrastColor,
    cx: "50",
    cy: "50",
    r: "50",
    strokeWidth: small ? '8' : '12',
    stroke: "currentColor"
  }), /*#__PURE__*/React__default.createElement("circle", {
    className: mainColor,
    cx: "50",
    cy: "50",
    r: "50",
    strokeWidth: small ? '8' : '12',
    stroke: "currentColor",
    strokeDasharray: "150%"
  })), !hideIcon && /*#__PURE__*/React__default.createElement(AoIcon, {
    icon: icon,
    className: mainColor,
    "aria-hidden": "true",
    size: small ? 'lg' : '2x'
  })), /*#__PURE__*/React__default.createElement("div", {
    className: classnames(!inheritTextStyle && ['text-body', messageColor], !message && 'sr-only'),
    role: "alert",
    "aria-live": "assertive"
  }, message || 'Loading, please wait.'));
});

var defaultStrengthConfig = {
  0: 'error',
  33: 'attention',
  60: 'success'
};
function getThresholdByPercent(strength, strengthConfig) {
  if (strengthConfig === void 0) {
    strengthConfig = defaultStrengthConfig;
  }

  var key = Object.keys(strengthConfig).sort(function (a, b) {
    return Number(b) - Number(a);
  }).find(function (percent) {
    return strength >= Number(percent);
  });
  return key ? strengthConfig[Number(key)] : undefined;
}
var DEFAULT_ELEMENT$4 = 'input';
var PasswordInput = /*#__PURE__*/React__default.forwardRef(function AOPasswordInput(_a, ref) {
  var _b = _a.as,
      as = _b === void 0 ? DEFAULT_ELEMENT$4 : _b,
      className = _a.className,
      id = _a.id,
      _c = _a.showStrength,
      showStrength = _c === void 0 ? false : _c,
      _d = _a.strength,
      strength = _d === void 0 ? 0 : _d,
      strengthConfig = _a.strengthConfig,
      isDisabled = _a["disabled"],
      ariaDescribedby = _a["aria-describedby"],
      state = _a.state,
      props = __rest(_a, ["as", "className", "id", "showStrength", "strength", "strengthConfig", 'disabled', 'aria-describedby', "state"]);

  var _e = useState(false),
      showPassword = _e[0],
      setShowPassword = _e[1];

  var strengthState = getThresholdByPercent(strength, strengthConfig);
  var indicatorBackground = {
    // TODO: Look at the classes for these colours
    'bg-gray-40': isDisabled,
    'bg-ongreen': !isDisabled && strengthState === 'success',
    'bg-rating': !isDisabled && strengthState === 'attention',
    'bg-deals': !isDisabled && strengthState === 'error'
  };
  var indicatorBorder = {
    'border-gray-40': isDisabled || !state,
    'border-success': !isDisabled && state === 'success',
    'border-attention': !isDisabled && state === 'attention',
    'border-error': !isDisabled && state === 'error',
    'border-highlight': !isDisabled && state === 'highlight'
  };
  return /*#__PURE__*/React__default.createElement("div", {
    className: classnames('inline-block rounded focus-within:shadow-outline-blue', className)
  }, /*#__PURE__*/React__default.createElement(InputContainer, {
    action: !isDisabled ? true : undefined,
    state: state,
    disabled: isDisabled,
    className: classnames('focus-within:shadow-none', {
      'rounded-b-none': showStrength
    })
  }, function (_a) {
    var inputProps = _a.inputProps,
        iconProps = _a.iconProps;
    return /*#__PURE__*/React__default.createElement(Fragment, null, /*#__PURE__*/React__default.createElement(Input, __assign({
      autoComplete: showStrength ? 'new-password' : 'current-password',
      "data-aods": "password-input"
    }, props, inputProps, {
      as: as,
      id: id,
      ref: ref,
      type: showPassword ? 'text' : 'password',
      "aria-describedby": showStrength || ariaDescribedby ? [showStrength && "".concat(id, "-strength-message"), ariaDescribedby].filter(Boolean).join(' ') : undefined
    })), !isDisabled && /*#__PURE__*/React__default.createElement(AoIcon, __assign({}, iconProps, {
      as: "button",
      type: "button",
      "aria-controls": id,
      className: classnames(iconProps.className, 'bg-transparent border-none cursor-pointer rounded-tr focus:shadow-outline-blue focus:outline-none text-secondary', {
        'rounded-br': !showStrength
      }),
      icon: showPassword ? 'hide' : 'show',
      label: showPassword ? 'Hide password' : 'Show password',
      onClick: function onClick() {
        return setShowPassword(function (isVisible) {
          return !isVisible;
        });
      }
    })));
  }), showStrength && /*#__PURE__*/React__default.createElement("div", {
    className: classnames('w-full overflow-hidden border border-t-0 border-solid rounded-b', indicatorBorder)
  }, /*#__PURE__*/React__default.createElement("div", {
    className: classnames('h-2', indicatorBackground),
    style: {
      width: "".concat(strength, "%")
    }
  }, /*#__PURE__*/React__default.createElement("span", {
    className: "sr-only",
    id: "".concat(id, "-strength-message"),
    "aria-live": "polite",
    "aria-atomic": true
  }, "Password strength ", strength, "%"))));
});

var RadioButton = /*#__PURE__*/React__default.forwardRef(function AORadioButton(props, ref) {
  return /*#__PURE__*/React__default.createElement(ToggleBase, __assign({}, props, {
    type: "radio-button",
    ref: ref
  }));
});

var RadioButtonGroup = function RadioButtonGroup(_a) {
  var children = _a.children,
      props = __rest(_a, ["children"]);

  return /*#__PURE__*/React__default.createElement(ToggleBaseGroup, __assign({}, props), children);
};

var DEFAULT_ELEMENT$3 = 'select';
var Select = /*#__PURE__*/React__default.forwardRef(function AOSelect(_a, ref) {
  var _b = _a.as,
      as = _b === void 0 ? DEFAULT_ELEMENT$3 : _b,
      children = _a.children,
      className = _a.className,
      state = _a.state,
      disabled = _a.disabled,
      props = __rest(_a, ["as", "children", "className", "state", "disabled"]);

  return /*#__PURE__*/React__default.createElement(InputContainer, {
    className: "bg-white",
    state: state,
    disabled: disabled,
    "data-aods": "select"
  }, function (_a) {
    var inputProps = _a.inputProps,
        iconProps = _a.iconProps;
    return /*#__PURE__*/React__default.createElement(Fragment, null, /*#__PURE__*/React__default.createElement(Input, __assign({}, props, inputProps, {
      as: as,
      className: classnames('appearance-none', inputProps.className, className),
      ref: ref
    }), children), /*#__PURE__*/React__default.createElement(AoIcon, __assign({}, iconProps, {
      icon: "chevron-down"
    })));
  });
});

var SummaryCard = /*#__PURE__*/React__default.forwardRef(function AOSummaryCard(_a, ref) {
  var children = _a.children,
      className = _a.className,
      inheritTextStyle = _a.inheritTextStyle,
      buttonText = _a.buttonText,
      _b = _a.buttonProps,
      buttonProps = _b === void 0 ? {} : _b,
      onButtonClick = _a.onButtonClick,
      props = __rest(_a, ["children", "className", "inheritTextStyle", "buttonText", "buttonProps", "onButtonClick"]);

  return /*#__PURE__*/React__default.createElement(Card, __assign({
    "data-aods": "summary-card"
  }, props, {
    className: className,
    ref: ref
  }), /*#__PURE__*/React__default.createElement("div", {
    className: classnames(!inheritTextStyle && 'text-body-sm', 'flex justify-between')
  }, children), buttonText && /*#__PURE__*/React__default.createElement(Button, __assign({}, buttonProps, {
    variant: "link",
    onClick: onButtonClick
  }), buttonText));
});

var DEFAULT_ELEMENT$2 = 'textarea';
var Textarea = /*#__PURE__*/React__default.forwardRef(function AOTextarea(_a, ref) {
  var _b = _a.as,
      as = _b === void 0 ? DEFAULT_ELEMENT$2 : _b,
      props = __rest(_a, ["as"]);

  return /*#__PURE__*/React__default.createElement(Input, __assign({
    "data-aods": "textarea"
  }, props, {
    as: as,
    ref: ref
  }));
});

function useFocusAway(ref, onFocusAway) {
  useEffect(function () {
    if (!document || !ref.current || !onFocusAway) return;

    function handleFocusOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        onFocusAway === null || onFocusAway === void 0 ? void 0 : onFocusAway(event);
      }
    }

    document.addEventListener('focusin', handleFocusOutside); // eslint-disable-next-line consistent-return

    return function () {
      document.removeEventListener('focusin', handleFocusOutside);
    };
  }, [ref, onFocusAway]);
}

var translations = {
  en: {
    close: function close() {
      return 'Close';
    }
  },
  de: {
    close: function close() {
      return 'Schließen';
    }
  }
};

var DEFAULT_ELEMENT$1 = 'div';
var TOOLTIP_OFFSET = 4;
var ARROW_SIZE = 8;
var ARROW_OFFSET = 4; // Prevent it from being placed against rounded corner

var tooltipPositions = ['top', 'bottom', 'left', 'right'];
var tooltipPositionToArrowDirection = {
  top: 'down',
  bottom: 'up',
  left: 'right',
  right: 'left'
};

var getArrowDirection = function getArrowDirection(popperAttributes) {
  var _a;

  var arrowDirection = '';
  var placement = (_a = popperAttributes === null || popperAttributes === void 0 ? void 0 : popperAttributes.popper) === null || _a === void 0 ? void 0 : _a['data-popper-placement'];
  if (!placement) return arrowDirection;
  tooltipPositions.forEach(function (position) {
    if (placement.startsWith(position)) {
      arrowDirection = tooltipPositionToArrowDirection[position];
    }
  });
  return arrowDirection;
}; // TODO: arrow currently doesn't have a shadow due to being CSS shape, replace with rotated square


var Tooltip = /*#__PURE__*/React__default.forwardRef(function AOTooltip(_a, ref) {
  var _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$1 : _b,
      children = _a.children,
      className = _a.className,
      _c = _a.placement,
      placement = _c === void 0 ? 'auto' : _c,
      _d = _a.referenceElement,
      referenceElement = _d === void 0 ? null : _d,
      hideArrow = _a.hideArrow,
      isOpen = _a.isOpen,
      hideCloseButton = _a.hideCloseButton,
      onClose = _a.onClose,
      onClickAway = _a.onClickAway,
      title = _a.title,
      _e = _a.maxWidth,
      maxWidth = _e === void 0 ? 'xs' : _e,
      props = __rest(_a, ["as", "children", "className", "placement", "referenceElement", "hideArrow", "isOpen", "hideCloseButton", "onClose", "onClickAway", "title", "maxWidth"]);

  var language = useConfig().language;
  var t = useTranslation(language, translations);
  var tooltipElement = useForwardedRef(ref);

  var _f = useState(null),
      arrowElement = _f[0],
      setArrowElement = _f[1];

  var _g = useState(null),
      arrowBorderElement = _g[0],
      setArrowBorderElement = _g[1];

  var _h = usePopper(referenceElement, tooltipElement.current, {
    placement: placement,
    modifiers: [{
      name: 'offset',
      options: {
        offset: [0, TOOLTIP_OFFSET + ARROW_SIZE]
      }
    }, {
      name: 'arrow',
      options: {
        element: arrowElement,
        padding: ARROW_OFFSET + 1
      }
    }, {
      name: 'arrow',
      options: {
        element: arrowBorderElement,
        padding: ARROW_OFFSET
      }
    }]
  }),
      styles = _h.styles,
      attributes = _h.attributes;

  var arrowDirection = useMemo(function () {
    return getArrowDirection(attributes);
  }, [attributes]);
  useClickAway(tooltipElement, isOpen ? onClickAway || onClose : undefined);
  useFocusAway(tooltipElement, isOpen ? onClickAway || onClose : undefined);
  useKeyDown({
    isEnabled: isOpen,
    key: 'Escape',
    onKeyDown: onClose
  });
  return /*#__PURE__*/React__default.createElement("span", {
    role: "status"
  }, /*#__PURE__*/React__default.createElement(ElementType, __assign({
    "data-aods": "tooltip"
  }, props, {
    className: classnames('text-body bg-white border border-solid border-gray-40 rounded transition-opacity duration-200 shadow-2dp', isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none', maxWidth !== 'none' && "max-w-".concat(maxWidth), className),
    role: "tooltip",
    ref: tooltipElement,
    style: styles.popper
  }, attributes.popper), (title || !hideCloseButton) && /*#__PURE__*/React__default.createElement("div", {
    className: "flex items-center justify-between pl-4"
  }, /*#__PURE__*/React__default.isValidElement(title) && /*#__PURE__*/React__default.cloneElement(title, {
    className: classnames('m-0', title.props.className)
  }), !hideCloseButton && /*#__PURE__*/React__default.createElement(Button, {
    className: "ml-auto",
    variant: "tertiary",
    icon: true,
    onClick: onClose,
    tabIndex: isOpen ? undefined : -1,
    "aria-label": t('close')
  }, /*#__PURE__*/React__default.createElement(AoIcon, {
    icon: "close",
    size: "xl"
  }))), /*#__PURE__*/React__default.createElement("div", {
    className: "p-4"
  }, children), !hideArrow && /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement("div", {
    className: classnames('arrow triangle triangle-sm triangle-offset text-gray-40', "triangle-".concat(arrowDirection)),
    ref: setArrowBorderElement,
    style: styles.arrow
  }), /*#__PURE__*/React__default.createElement("div", {
    className: classnames('arrow triangle triangle-bordered triangle-sm triangle-offset text-white', (arrowDirection === 'left' || arrowDirection === 'right') && 'mt-px', (arrowDirection === 'up' || arrowDirection === 'down') && 'ml-px', "triangle-".concat(arrowDirection)),
    ref: setArrowElement,
    style: styles.arrow
  }))));
});

var DEFAULT_ELEMENT = 'div';
var TooltipManager = /*#__PURE__*/React__default.forwardRef(function AOTooltipManager(_a, ref) {
  var _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT : _b,
      children = _a.children,
      id = _a.id,
      _c = _a.defaultOpen,
      defaultOpen = _c === void 0 ? false : _c,
      _d = _a.openDelay,
      openDelay = _d === void 0 ? 0 : _d,
      _e = _a.closeDelay,
      closeDelay = _e === void 0 ? 1500 : _e,
      _f = _a.referenceTriggers,
      referenceTriggers = _f === void 0 ? ['click', 'focus', 'hover'] : _f,
      props = __rest(_a, ["as", "children", "id", "defaultOpen", "openDelay", "closeDelay", "referenceTriggers"]);

  var _g = useState(null),
      referenceElement = _g[0],
      setReferenceElement = _g[1];

  var _h = useState(defaultOpen),
      isOpen = _h[0],
      setOpen = _h[1];

  var enterTimer = React__default.useRef();
  var leaveTimer = React__default.useRef();
  var tooltipId = "".concat(id, "-tooltip");

  var handleOpen = function handleOpen() {
    setOpen(true);
    window.clearTimeout(enterTimer.current);
  };

  var handleClose = function handleClose() {
    setOpen(false);
  };

  var handleEnter = function handleEnter() {
    window.clearTimeout(enterTimer.current);
    window.clearTimeout(leaveTimer.current);
    enterTimer.current = window.setTimeout(function () {
      handleOpen();
    }, openDelay);
  };

  var handleLeave = function handleLeave() {
    window.clearTimeout(enterTimer.current);
    window.clearTimeout(leaveTimer.current);
    leaveTimer.current = window.setTimeout(function () {
      handleClose();
    }, closeDelay);
  };

  var handleTooltipEnter = function handleTooltipEnter() {
    window.clearTimeout(enterTimer.current);
    window.clearTimeout(leaveTimer.current);
  };

  var renderProps = {
    referenceProps: __assign({
      'aria-describedby': tooltipId,
      'ref': setReferenceElement
    }, referenceTriggers.reduce(function (triggerProps, trigger) {
      switch (trigger) {
        case 'click':
          return __assign(__assign({}, triggerProps), {
            onClick: handleOpen
          });

        case 'focus':
          return __assign(__assign({}, triggerProps), {
            onFocus: handleEnter,
            onBlur: handleLeave
          });

        case 'hover':
          return __assign(__assign({}, triggerProps), {
            onMouseEnter: handleEnter,
            onMouseLeave: handleLeave
          });

        default:
          return triggerProps;
      }
    }, {})),
    tooltipProps: {
      id: tooltipId,
      referenceElement: referenceElement,
      isOpen: isOpen,
      onClose: handleClose,
      onClickAway: handleLeave,
      onFocus: handleTooltipEnter,
      onMouseOver: handleTooltipEnter
    },
    isOpen: isOpen,
    openTooltip: handleOpen,
    closeTooltip: handleClose,
    tabIndex: isOpen ? undefined : -1
  };
  return /*#__PURE__*/React__default.createElement(ElementType, __assign({
    "data-aods": "tooltip-manager"
  }, props, {
    ref: ref
  }), children(renderProps));
});

export { Accordion, AccordionContent, AccordionHeader, AccordionItem, Breadcrumb, BreadcrumbItem, Button, Card, CardDivider, Checkbox, CheckboxGroup, Container, DesignSystemProvider, Drawer, EnergyLabel, Field, FieldSet, Grid, GridItem, AoIcon as Icon, Input, InputContainer, InputMessage, Label, Legend, LoadingSpinner, Modal, PasswordInput, RadioButton, RadioButtonGroup, Select, SummaryCard, Tag, Textarea, Tooltip, TooltipManager };
//# sourceMappingURL=components-react.esm.js.map
